import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { SourceBadge } from '../components/SourceBadge';
import { playBellSound, BELL_LABEL, BELL_ICON } from '../utils/bellSounds';
import { LogOut, UserPlus, Users, Clock, ArrowRight, AlertCircle, Settings, UserCheck, Volume2, VolumeX, Thermometer, Baby, Printer, Edit2, Check, X, ClipboardList, Heart, Activity, Tv, CheckCircle, FastForward, Undo2, Timer, History, Calendar, Play, Power, ChevronLeft, ChevronRight, Trash2, Search, Download, Menu, MapPin, FileText, User, Bell } from 'lucide-react';
import DoctorSettings from '@/components/DoctorSettings';
import PromoSettings from '@/components/PromoSettings';
import { safePrintWindow, escapeHtml } from '../utils/printUtils';
import { vitalsTableHtml, isPaedsPatient, formatBp } from '../utils/vitals';
import LiveClock from '@/components/LiveClock';
import { VitalsEditGrid } from '@/components/VitalsEditGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleR8LMPi7v9G9bRwLKvqruru3gToVIvxwjaOjl1oVGfxpd6Chn2gXFvxnd6CdnWcaF/xpd5+cnGYbGfxsdp+cmWIcGfxtdZ6amV8dGfxudZqXl1wdGvxvdZiVlVkfG/xwdJWSklYgHPxxc5KPkFQiHvxycpCMjVMjH/xzcY6KilEkIPx0cIuIiE8lIfx1b4mGhk0mIvx2boaEhEsnI/x3bYSCgkkoJPx4bIKAgEgpJfx5a4B+fkYqJvx6an1 8e0QrJ/x7aXp6eEIuKPx8aHh4dkAvKfx9Z3V1dD4wKvx+ZnNzcjwyK/x/ZXFxcDszLPyAZG9vbjk0LfyBY21tazc1LvyCYmtraWk2Ny/8g2FpaWZlNzgx/IRgZ2dkYzg5Mv+FX2VlYmE5OjP/hlxjY2BfOTs0/4dcYWFfXTo8Nf+IW19fXVs7PTb/iVpeXltaOz83/4pZXFxZWDw/OP+LWFtbV1Y8QDn/jFdZWVVVPUI7/41WV1dTUz1DPP+OVVZWUlI+RD3/j1RUVFBQP0U+/5BTUVFPUEBGP/+RUlBPTk5BR0D/kk9OTk1MQkhC/5NNTU1LSklJQ/+US0tKSUhKSkT/lUpJSEdGS0tF/5ZJR0dGRUxMRv+XR0ZFRERLTUXA';

const StaffDashboard = () => {
  const { user, token, logout } = useAuth();
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [fever, setFever] = useState(false);
  const [review, setReview] = useState(false);
  const [diabetes, setDiabetes] = useState(false);
  const [contactNumber, setContactNumber] = useState('');
  const [place, setPlace] = useState('');
  const [vitalBp, setVitalBp] = useState('');
  const [vitalPr, setVitalPr] = useState('');
  const [vitalSpo2, setVitalSpo2] = useState('');
  const [vitalTemp, setVitalTemp] = useState('');
  const [vitalWeight, setVitalWeight] = useState('');
  const [entrySource, setEntrySource] = useState('walk_in'); // walk_in | phone | attender — unified entry source
  const [htn, setHtn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [nextToken, setNextToken] = useState('');
  const [tokenSystemMode, setTokenSystemMode] = useState('common');
  const [nextTokenGeneral, setNextTokenGeneral] = useState('');
  const [nextTokenPaediatric, setNextTokenPaediatric] = useState('');
  const [daySummary, setDaySummary] = useState({ total: 0, completed: 0, missed: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showPromoSettings, setShowPromoSettings] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastAddedPatient, setLastAddedPatient] = useState(null);
  const [editingPatientId, setEditingPatientId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingAge, setEditingAge] = useState('');
  const [editingSex, setEditingSex] = useState('');
  const [editingUrgent, setEditingUrgent] = useState(false);
  const [editingFever, setEditingFever] = useState(false);
  const [onDutyDoctor, setOnDutyDoctor] = useState(null);
  const [editingVitals, setEditingVitals] = useState({ bp: '', pr: '', spo2: '', temp: '', weight: '' });

  // Daily Rx Start anchor state
  const [rxAnchorInfo, setRxAnchorInfo] = useState(null);
  const [showRxAnchorModal, setShowRxAnchorModal] = useState(false);
  const [rxAnchorValue, setRxAnchorValue] = useState('');

  // Pre-registration (Phone / Attender) state
  const [pendingArrival, setPendingArrival] = useState([]);
  const [showPreRegModal, setShowPreRegModal] = useState(false);
  const [preRegSource, setPreRegSource] = useState('phone'); // phone | attender
  const [preRegForm, setPreRegForm] = useState({ name: '', age: '', sex: '', contact_number: '', place: '', notes: '' });
  
  // ER Bed selection state
  const [showERBedModal, setShowERBedModal] = useState(false);
  const [erPatientId, setErPatientId] = useState(null);
  const [erEditMode, setErEditMode] = useState(false); // true when editing existing ER patient's bed
  const [occupiedBeds, setOccupiedBeds] = useState({});
  const ER_BEDS = ['B1', 'B2', 'C1', 'C2', 'C3', 'DB', 'OT'];
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'adult', 'paeds'
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcement1, setAnnouncement1] = useState('');
  const [announcement2, setAnnouncement2] = useState('');
  const [scroll1Enabled, setScroll1Enabled] = useState(true);
  const [scroll2Enabled, setScroll2Enabled] = useState(true);
  const lastPatientIdRef = useRef(null);
  const audioRef = useRef(null);

  // Auto-call system state
  const [autoCallEnabled, setAutoCallEnabled] = useState(false);
  const [suggestedNext, setSuggestedNext] = useState(null);
  const [autoCallLoading, setAutoCallLoading] = useState(false);

  // Past records state
  const [showPastRecords, setShowPastRecords] = useState(false);
  const [pastRecordsDate, setPastRecordsDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [pastPatients, setPastPatients] = useState([]);
  const [pastSummary, setPastSummary] = useState(null);
  const [loadingPastRecords, setLoadingPastRecords] = useState(false);

  // Delete day state
  const [showDeleteDay, setShowDeleteDay] = useState(false);
  const [deleteDaySummary, setDeleteDaySummary] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmDate, setDeleteConfirmDate] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Diabetic Registry state
  const [showDiabeticRegistry, setShowDiabeticRegistry] = useState(false);
  const [diabeticList, setDiabeticList] = useState([]);
  const [diabeticLoading, setDiabeticLoading] = useState(false);
  const [diabeticSearch, setDiabeticSearch] = useState('');
  const [selectedDiabetic, setSelectedDiabetic] = useState(null);

  // DM inline search state (for add patient form)
  const [dmInlineSearch, setDmInlineSearch] = useState('');
  const [dmInlineResults, setDmInlineResults] = useState([]);
  const [dmInlineLoading, setDmInlineLoading] = useState(false);
  const [selectedDmEntry, setSelectedDmEntry] = useState(null); // selected existing DM entry to link

  // Add-to-DM modal state (for adding existing patient to DM registry)
  const [showAddToDmModal, setShowAddToDmModal] = useState(false);
  const [addToDmPatient, setAddToDmPatient] = useState(null);
  const [addToDmSearch, setAddToDmSearch] = useState('');
  const [addToDmResults, setAddToDmResults] = useState([]);
  const [addToDmLoading, setAddToDmLoading] = useState(false);

  // Places list & phone lookup state
  const [placesList, setPlacesList] = useState([]);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const [placeFilter, setPlaceFilter] = useState('');
  const [phoneLookup, setPhoneLookup] = useState(null);
  const [phoneLookupLoading, setPhoneLookupLoading] = useState(false);
  const phoneLookupTimer = useRef(null);
  // Pre-register modal: separate lookup state + place dropdown
  const [preRegPhoneLookup, setPreRegPhoneLookup] = useState(null);
  const [preRegPhoneLoading, setPreRegPhoneLoading] = useState(false);
  const [preRegShowPlaceDropdown, setPreRegShowPlaceDropdown] = useState(false);
  const [preRegPlaceFilter, setPreRegPlaceFilter] = useState('');

  // Camp patient state
  const [showCampEntry, setShowCampEntry] = useState(false);
  const [campPatients, setCampPatients] = useState([]);
  const [campName, setCampName] = useState('');
  const [campAge, setCampAge] = useState('');
  const [campSex, setCampSex] = useState('');
  const [campPhone, setCampPhone] = useState('');
  const [campPlace, setCampPlace] = useState('');
  const [campNotes, setCampNotes] = useState('');
  const [campLoading, setCampLoading] = useState(false);
  const [campType, setCampType] = useState('respiratory');
  const [campFilterType, setCampFilterType] = useState('');
  const [campStartDate, setCampStartDate] = useState('');
  const [campEndDate, setCampEndDate] = useState('');

  // Review reminders state
  const [showReviews, setShowReviews] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  // Doctor → staff bell alert state
  const [activeBell, setActiveBell] = useState(null); // {id, type, note, rung_by, rung_at}
  const bellStopRef = useRef(null);
  const seenBellIdRef = useRef(null);

  // Places management state
  const [showPlacesManager, setShowPlacesManager] = useState(false);
  const [placesPassword, setPlacesPassword] = useState('');
  const [placesUnlocked, setPlacesUnlocked] = useState(false);
  const [newPlaceName, setNewPlaceName] = useState('');

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
  }, []);

  // Fetch current announcements
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/public/announcements`);
      setAnnouncement1(response.data.line1 || '');
      setAnnouncement2(response.data.line2 || '');
      setScroll1Enabled(response.data.scroll1_enabled !== false);
      setScroll2Enabled(response.data.scroll2_enabled !== false);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  // Save announcements
  const saveAnnouncements = async () => {
    try {
      await axios.put(
        `${API}/announcements`,
        { 
          line1: announcement1, 
          line2: announcement2,
          scroll1_enabled: scroll1Enabled,
          scroll2_enabled: scroll2Enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Announcements updated!');
      setShowAnnouncementModal(false);
    } catch (error) {
      toast.error('Failed to update announcements');
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  // Helper to parse age to months for categorization
  const parseAgeToMonths = (ageStr) => {
    if (!ageStr) return null;
    const str = ageStr.toString().toLowerCase().trim();
    if (str.endsWith('d')) {
      const days = parseInt(str);
      return isNaN(days) ? null : days / 30;
    } else if (str.endsWith('m')) {
      const months = parseInt(str);
      return isNaN(months) ? null : months;
    } else if (str.endsWith('y')) {
      const years = parseInt(str);
      return isNaN(years) ? null : years * 12;
    } else {
      const years = parseInt(str);
      return isNaN(years) ? null : years * 12;
    }
  };

  // Categorize patients by age
  const getPatientCategory = (patient) => {
    const months = parseAgeToMonths(patient.age);
    if (months === null) return 'unknown';
    if (months < 60) return 'infants'; // Under 5 years
    if (months < 144) return 'children'; // 5-11 years
    return 'adults'; // 12+ years
  };

  // Categorize patients by token-series prefix (A=Adult / P=Paeds)
  // Legacy W-/O-/G- tokens (issued before the A/P rule) fall back to Adult bucket.
  const getPatientSeries = (patient) => {
    const t = (patient.token_number || '').split('-')[0];
    if (t === 'P') return 'paeds';
    return 'adult';
  };

  // Trigger flash and sound alert
  const triggerAlert = () => {
    setIsFlashing(true);
    playNotificationSound();
    // Play sound 3 times with delay
    setTimeout(() => playNotificationSound(), 500);
    setTimeout(() => playNotificationSound(), 1000);
    // Stop flashing after animation
    setTimeout(() => setIsFlashing(false), 1500);
  };

  const fetchQueue = async () => {
    try {
      const response = await axios.get(`${API}/patients/queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
      
      // Check for patient in cabin
      const inCabinPatient = response.data.find(p => p.status === 'in_cabin');
      
      // Detect if a NEW patient just entered cabin (trigger alert)
      if (inCabinPatient && inCabinPatient.id !== lastPatientIdRef.current) {
        setCurrentPatient(inCabinPatient);
        if (lastPatientIdRef.current !== null) {
          // Only trigger alert if this isn't the initial load
          triggerAlert();
          toast.success(`Patient ${inCabinPatient.name} (${inCabinPatient.token_number}) called to cabin!`, {
            duration: 5000
          });
        }
        lastPatientIdRef.current = inCabinPatient.id;
      } else if (!inCabinPatient) {
        setCurrentPatient(null);
        lastPatientIdRef.current = null;
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
    }
  };

  // Daily Rx Start helpers
  const fetchRxAnchor = async () => {
    try {
      const r = await axios.get(`${API}/prescription/daily-start`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRxAnchorInfo(r.data);
    } catch (_) { /* ignore */ }
  };

  const openRxAnchorModal = async () => {
    await fetchRxAnchor();
    setRxAnchorValue('');
    setShowRxAnchorModal(true);
  };

  const saveRxAnchor = async () => {
    const v = (rxAnchorValue || '').trim().replace(/\s+/g, '');
    if (!v) { toast.error('Enter a number like A2571'); return; }
    if (!/^[A-Za-z]+\d{1,5}$/.test(v)) { toast.error('Format: letter(s) + 1–5 digits'); return; }
    try {
      await axios.post(
        `${API}/prescription/daily-start`,
        { start_number: v },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Today's Rx start set to ${v}`);
      setShowRxAnchorModal(false);
      setRxAnchorValue('');
      await fetchRxAnchor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const clearRxAnchor = async () => {
    try {
      await axios.post(
        `${API}/prescription/daily-start`,
        { start_number: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rx start cleared');
      setShowRxAnchorModal(false);
      await fetchRxAnchor();
    } catch (_) { toast.error('Failed to clear'); }
  };

  // ===== Pre-registration helpers =====
  const fetchPendingArrival = async () => {
    try {
      const r = await axios.get(`${API}/patients/pending-arrival`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingArrival(r.data || []);
    } catch (_) { /* ignore */ }
  };

  const openPreRegModal = (source) => {
    setPreRegSource(source);
    setPreRegForm({ name: '', age: '', sex: '', contact_number: '', place: '', notes: '' });
    setPreRegPhoneLookup(null);
    setPreRegShowPlaceDropdown(false);
    setPreRegPlaceFilter('');
    setShowPreRegModal(true);
  };

  const submitPreReg = async () => {
    if (!preRegForm.name?.trim()) { toast.error('Name required'); return; }
    try {
      const r = await axios.post(
        `${API}/patients/pre-register`,
        {
          name: preRegForm.name.trim(),
          age: preRegForm.age || '',
          sex: preRegForm.sex || '',
          contact_number: preRegForm.contact_number || '',
          place: preRegForm.place || '',
          arrival_source: preRegSource,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const prov = r.data?.provisional_id || 'PRE-?';
      toast.success(`${preRegSource === 'phone' ? 'Phone booking' : 'Attender booking'} saved · ${prov}`);
      setShowPreRegModal(false);
      await fetchPendingArrival();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pre-register');
    }
  };



  const markPendingArrived = async (pid) => {
    try {
      const r = await axios.patch(`${API}/patients/${pid}/mark-arrived`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Token issued · ${r.data.token_number}`);
      await fetchPendingArrival();
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark arrived');
    }
  };

  const cancelPending = async (pid) => {
    if (!window.confirm('Cancel this pre-registration?')) return;
    try {
      await axios.delete(`${API}/patients/${pid}/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pre-registration cancelled');
      await fetchPendingArrival();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const fetchNextToken = async () => {
    try {
      const response = await axios.get(`${API}/settings/next-tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // New A/P system: always show Adult walk-in next + Paeds next as the preview
      setTokenSystemMode(response.data.mode || 'ap');
      setNextTokenGeneral(response.data.next_adult_walkin || response.data.next_general || '');
      setNextTokenPaediatric(response.data.next_paeds || response.data.next_paediatric || '');
      setNextToken(response.data.next_adult_walkin || response.data.next_general || '');
    } catch (error) {
      console.error('Error fetching next token:', error);
    }
  };

  const fetchOnDutyDoctor = async () => {
    try {
      const r = await axios.get(`${API}/doctors/on-duty`);
      setOnDutyDoctor(r.data || null);
    } catch (_) { setOnDutyDoctor(null); }
  };

  const fetchDaySummary = async () => {
    try {
      const response = await axios.get(`${API}/patients/day-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDaySummary(response.data);
    } catch (error) {
      console.error('Error fetching day summary:', error);
    }
  };

  useEffect(() => {
    fetchQueue();
    fetchNextToken();
    fetchDaySummary();
    fetchSuggestedNext();
    fetchPlacesList();
    fetchRxAnchor();
    fetchPendingArrival();
    fetchOnDutyDoctor();
    const interval = setInterval(() => {
      fetchQueue();
      fetchOnDutyDoctor();
      fetchDaySummary();
      fetchRxAnchor();
      fetchPendingArrival();
      if (autoCallEnabled) {
        fetchSuggestedNext();
      }
    }, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [autoCallEnabled]);

  // Poll for bell calls from doctor (every 3s) and play sound on a NEW call
  useEffect(() => {
    let alive = true;
    const fetchBell = async () => {
      try {
        const r = await axios.get(`${API}/bell/latest`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!alive) return;
        const call = r.data || null;
        if (call && call.id !== seenBellIdRef.current) {
          seenBellIdRef.current = call.id;
          setActiveBell(call);
          if (bellStopRef.current) bellStopRef.current();
          bellStopRef.current = playBellSound(call.type);
        }
      } catch (_) { /* ignore */ }
    };
    fetchBell();
    const id = setInterval(fetchBell, 3000);
    return () => { alive = false; clearInterval(id); if (bellStopRef.current) bellStopRef.current(); };
  }, [token]);

  const acknowledgeBell = async () => {
    if (!activeBell) return;
    if (bellStopRef.current) bellStopRef.current();
    bellStopRef.current = null;
    try {
      await axios.patch(`${API}/bell/${activeBell.id}/ack`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) { /* idempotent on server */ }
    setActiveBell(null);
  };

  // Fetch suggested next patient for auto-call
  const fetchSuggestedNext = async () => {
    try {
      const response = await axios.get(`${API}/patients/suggest-next`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestedNext(response.data.has_next ? response.data.patient : null);
    } catch (error) {
      console.error('Error fetching suggested next:', error);
    }
  };

  // Fetch places list
  const fetchPlacesList = async () => {
    try {
      const response = await axios.get(`${API}/settings/places`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlacesList(response.data.places || []);
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  // Phone number lookup (manual trigger)
  const handlePhoneChange = (val) => {
    setContactNumber(val);
    if (!val.trim()) setPhoneLookup(null);
  };

  const searchPhoneLookup = async () => {
    if (contactNumber.trim().length < 4) { toast.error('Enter at least 4 digits'); return; }
    setPhoneLookupLoading(true);
    try {
      const response = await axios.get(`${API}/patients/by-phone/${encodeURIComponent(contactNumber.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhoneLookup(response.data.family?.length > 0 ? response.data : null);
      if (!response.data.family?.length) toast('No previous records found');
    } catch { setPhoneLookup(null); }
    finally { setPhoneLookupLoading(false); }
  };

  const selectFamilyMember = (member) => {
    setName(member.name || '');
    setAge(member.age || '');
    setSex(member.sex || '');
    if (member.place) setPlace(member.place);
    toast.success(`Loaded: ${member.name}`);
  };

  // Pre-register modal: phone search → previous-visit family members
  const searchPreRegPhone = async () => {
    const phone = (preRegForm.contact_number || '').trim();
    if (phone.length < 4) { toast.error('Enter at least 4 digits'); return; }
    setPreRegPhoneLoading(true);
    try {
      const r = await axios.get(`${API}/patients/by-phone/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreRegPhoneLookup(r.data.family?.length > 0 ? r.data : null);
      if (!r.data.family?.length) toast('No previous records found');
    } catch { setPreRegPhoneLookup(null); }
    finally { setPreRegPhoneLoading(false); }
  };

  const selectPreRegFamilyMember = (member) => {
    setPreRegForm({
      ...preRegForm,
      name: member.name || preRegForm.name,
      age: member.age || preRegForm.age,
      sex: member.sex || preRegForm.sex,
      place: member.place || preRegForm.place,
    });
    toast.success(`Loaded: ${member.name}`);
  };

  const selectPreRegPlace = (pl) => {
    setPreRegForm({ ...preRegForm, place: pl });
    setPreRegShowPlaceDropdown(false);
    setPreRegPlaceFilter('');
  };

  // Place selection
  const selectPlace = (pl) => {
    setPlace(pl);
    setShowPlaceDropdown(false);
    setPlaceFilter('');
  };

  const handleAddNewPlace = async (newPlace, password) => {
    try {
      const response = await axios.post(`${API}/settings/places`, { place: newPlace, password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlacesList(response.data.places || []);
      toast.success(`"${newPlace}" added`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add place');
      return false;
    }
  };

  const handleDeletePlace = async (placeName, password) => {
    try {
      const response = await axios.post(`${API}/settings/places/delete`, { place: placeName, password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlacesList(response.data.places || []);
      toast.success(`"${placeName}" removed`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove place');
      return false;
    }
  };

  const CAMP_TYPES = [
    { value: 'respiratory', label: 'Respiratory', prefix: 'RC' },
    { value: 'diabetic', label: 'Diabetic', prefix: 'DC' },
    { value: 'anemia', label: 'Anemia', prefix: 'AC' },
    { value: 'thyroid', label: 'Thyroid', prefix: 'TC' },
    { value: 'cholesterol', label: 'Cholesterol', prefix: 'CC' },
  ];

  // Camp patient functions
  const fetchCampPatients = async (filterType, startDate, endDate) => {
    try {
      let url = `${API}/camp-patients?`;
      if (startDate && endDate) {
        url += `start_date=${startDate}&end_date=${endDate}`;
      }
      if (filterType) url += `&camp_type=${filterType}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setCampPatients(response.data.entries || []);
    } catch { /* ignore */ }
  };

  const handleAddCampPatient = async (e) => {
    e.preventDefault();
    setCampLoading(true);
    try {
      await axios.post(`${API}/camp-patients`, {
        name: campName.trim(), age: campAge.trim(), sex: campSex,
        contact_number: campPhone.trim(), place: campPlace.trim(),
        camp_type: campType, notes: campNotes.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Camp patient added');
      setCampName(''); setCampAge(''); setCampSex(''); setCampPhone(''); setCampPlace(''); setCampNotes('');
      fetchCampPatients(campFilterType, campStartDate, campEndDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add camp patient');
    } finally { setCampLoading(false); }
  };

  const handleDeleteCampPatient = async (id, serial) => {
    if (!window.confirm(`Delete camp patient ${serial}?`)) return;
    try {
      await axios.delete(`${API}/camp-patients/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${serial}`);
      fetchCampPatients(campFilterType, campStartDate, campEndDate);
    } catch { toast.error('Failed to delete'); }
  };

  const downloadCampPDF = async () => {
    let url = `${API}/camp-patients/export?`;
    if (campStartDate && campEndDate) url += `start_date=${campStartDate}&end_date=${campEndDate}`;
    if (campFilterType) url += `&camp_type=${campFilterType}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data;
      const rows = data.entries.map((e, i) => `<tr>
        <td style="border:1px solid #ccc;padding:6px">${i + 1}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.serial_number)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.date)}</td>
        <td style="border:1px solid #ccc;padding:6px;font-weight:bold">${escapeHtml(e.name) || '-'}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.age)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.sex)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.contact_number) || '-'}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.place) || '-'}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml((CAMP_TYPES.find(c => c.value === e.camp_type) || {}).label || e.camp_type)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.notes) || '-'}</td>
      </tr>`).join('');
      const styles = 'body{font-family:Arial,sans-serif;padding:20px}table{border-collapse:collapse;width:100%}th{background:#1a5632;color:white;padding:8px;border:1px solid #ccc;text-align:left}h1{color:#1a5632}h3{color:#555}';
      const body = `<h1>K.M. Multi Speciality Clinic, Koliyanur</h1>
        <h2>${escapeHtml(data.title)}</h2><h3>Period: ${escapeHtml(data.period)} | Total Patients: ${data.total_patients}</h3>
        <table><thead><tr><th>#</th><th>Serial</th><th>Date</th><th>Name</th><th>Age</th><th>Sex</th><th>Phone</th><th>Place</th><th>Camp</th><th>Notes</th></tr></thead>
        <tbody>${rows}</tbody></table>
        <p style="margin-top:20px;color:#888;font-size:12px">Generated on ${new Date().toLocaleString('en-IN')}</p>`;
      safePrintWindow(`Camp Report - ${data.title}`, `<style>${styles}</style>${body}`);
    } catch { toast.error('Failed to generate report'); }
  };

  // Review reminders
  const fetchReviews = async () => {
    setReviewLoading(true);
    try {
      const response = await axios.get(`${API}/reviews/upcoming?days_ahead=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewData(response.data);
    } catch { toast.error('Failed to load reviews'); }
    finally { setReviewLoading(false); }
  };

  const printReviewPDF = () => {
    if (!reviewData) return;
    const all = [...(reviewData.overdue || []), ...(reviewData.upcoming || [])];
    const rows = all.map((p, i) => {
      const isOverdue = reviewData.overdue?.some(o => o.id === p.id);
      return `<tr style="${isOverdue ? 'background:#fff5f5' : ''}">
        <td style="border:1px solid #ddd;padding:6px">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:6px;font-weight:bold">${escapeHtml(p.name) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.age)} ${escapeHtml(p.sex)}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.contact_number) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.place) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.diabetic_id) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;font-weight:bold;color:${isOverdue ? '#dc2626' : '#1d4ed8'}">${p.review_date}${isOverdue ? ' (OVERDUE)' : ''}</td>
        <td style="border:1px solid #ddd;padding:6px">${p.date || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;width:80px"></td>
      </tr>`;
    }).join('');
    const styles = `body{font-family:Arial,sans-serif;padding:20px}h1{color:#7f1d1d}table{border-collapse:collapse;width:100%;font-size:10pt}th{background:#7f1d1d;color:white;padding:8px;border:1px solid #ddd;text-align:left}.stat{display:inline-block;padding:4px 12px;border-radius:4px;font-weight:bold;margin-right:8px}`;
    const body = `<h1>K.M. Multi Speciality Clinic, Koliyanur</h1>
      <h2>Review Reminder Call List</h2>
      <p>Generated: ${new Date().toLocaleString('en-IN')}</p>
      <p><span class="stat" style="background:#fee2e2;color:#dc2626">Overdue: ${reviewData.overdue_count}</span>
         <span class="stat" style="background:#dbeafe;color:#1d4ed8">Upcoming (3 days): ${reviewData.upcoming_count}</span></p>
      <table><thead><tr><th>#</th><th>Name</th><th>Age/Sex</th><th>Phone</th><th>Place</th><th>DM ID</th><th>Review Date</th><th>Last Visit</th><th>Called?</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <p style="margin-top:15px;color:#888;font-size:9pt">Please call and remind patients about their upcoming review appointment.</p>`;
    safePrintWindow('Review Reminders - K.M Clinic', `<style>${styles}</style>${body}`);
  };

  // Auto-call next patient (alternating P/G or sequential)
  const handleAutoCallNext = async () => {
    if (!autoCallEnabled) {
      toast.error('Auto-call is disabled. Turn it ON first.');
      return;
    }
    
    setAutoCallLoading(true);
    try {
      const response = await axios.post(
        `${API}/patients/auto-call-next`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.called) {
        toast.success(`Called: ${response.data.patient.token_number} - ${response.data.patient.name || 'Nameless'}`);
        triggerAlert();
        fetchQueue();
        fetchSuggestedNext();
      } else {
        toast.info(response.data.message || 'No patients waiting');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to call next patient');
    } finally {
      setAutoCallLoading(false);
    }
  };

  // Fetch past records
  const fetchPastRecords = async (dateStr) => {
    setLoadingPastRecords(true);
    try {
      const [patientsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/patients/export/date?date_str=${dateStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/patients/summary-by-date?date_str=${dateStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPastPatients(patientsRes.data.patients || []);
      setPastSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching past records:', error);
      toast.error('Failed to fetch past records');
    } finally {
      setLoadingPastRecords(false);
    }
  };

  // Change past records date
  const changePastDate = (days) => {
    const current = new Date(pastRecordsDate);
    current.setDate(current.getDate() + days);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (current >= today) return; // Don't go to future or today
    const newDate = current.toISOString().split('T')[0];
    setPastRecordsDate(newDate);
    fetchPastRecords(newDate);
  };

  // Open past records modal
  const openPastRecords = () => {
    setShowPastRecords(true);
    fetchPastRecords(pastRecordsDate);
  };

  // Delete day functions
  const fetchDeleteDaySummary = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.get(`${API}/data/dates-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteDaySummary(response.data);
    } catch (error) {
      toast.error('Failed to load data summary');
    } finally {
      setDeleteLoading(false);
    }
  };

  const deletePatientsByDate = async (dateStr) => {
    try {
      const response = await axios.delete(`${API}/patients/by-date?date_str=${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setDeleteConfirmDate(null);
      fetchDeleteDaySummary();
      fetchQueue();
    } catch (error) {
      toast.error('Failed to delete patient data');
    }
  };

  const verifyDeletePassword = async () => {
    setPasswordError('');
    try {
      await axios.post(`${API}/verify-delete-password`, 
        { password: deletePassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordVerified(true);
      fetchDeleteDaySummary();
    } catch (error) {
      setPasswordError('Incorrect password');
    }
  };

  // Diabetic Registry functions
  const fetchDiabeticRegistry = async (search = '') => {
    setDiabeticLoading(true);
    try {
      const url = search ? `${API}/diabetic/search?q=${encodeURIComponent(search)}` : `${API}/diabetic/registry`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setDiabeticList(response.data);
    } catch (error) {
      toast.error('Failed to load diabetic registry');
    } finally {
      setDiabeticLoading(false);
    }
  };

  // DM inline search (for patient entry form)
  const searchDmInline = async (query) => {
    setDmInlineSearch(query);
    if (!query.trim()) { setDmInlineResults([]); return; }
    setDmInlineLoading(true);
    try {
      const response = await axios.get(`${API}/diabetic/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDmInlineResults(response.data);
    } catch { setDmInlineResults([]); }
    finally { setDmInlineLoading(false); }
  };

  const selectDmEntry = (entry) => {
    setSelectedDmEntry(entry);
    setContactNumber(entry.contact_number || '');
    setPlace(entry.place || '');
    setDmInlineSearch('');
    setDmInlineResults([]);
  };

  const clearDmSelection = () => {
    setSelectedDmEntry(null);
    setDmInlineSearch('');
    setDmInlineResults([]);
    setContactNumber('');
    setPlace('');
  };

  // Add existing patient to DM registry
  const openAddToDmModal = (patient) => {
    setAddToDmPatient(patient);
    setAddToDmSearch('');
    setAddToDmResults([]);
    setShowAddToDmModal(true);
  };

  const searchAddToDm = async (query) => {
    setAddToDmSearch(query);
    if (!query.trim()) { setAddToDmResults([]); return; }
    setAddToDmLoading(true);
    try {
      const response = await axios.get(`${API}/diabetic/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddToDmResults(response.data);
    } catch { setAddToDmResults([]); }
    finally { setAddToDmLoading(false); }
  };

  const linkPatientToDm = async (patientId, diabeticId) => {
    try {
      await axios.post(`${API}/diabetic/link-patient/${patientId}/${diabeticId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Patient linked to DM registry & visit recorded');
      setShowAddToDmModal(false);
      setAddToDmPatient(null);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link patient');
    }
  };

  const registerPatientAsDm = async (patientId) => {
    try {
      const response = await axios.post(`${API}/diabetic/register-from-patient/${patientId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Registered in DM Registry: ${response.data.diabetic_id}`);
      setShowAddToDmModal(false);
      setAddToDmPatient(null);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register in DM registry');
    }
  };

  const handleAddPatient = async (e) => {
    e.preventDefault();
    // All fields are now optional for quick token generation

    // Validate age format only if age is provided - supports decimals like 1.6y, 2.3y
    if (age.trim()) {
      const agePattern = /^(\d+\.?\d*)(d|m|y)?$/i;
      if (!agePattern.test(age.trim())) {
        toast.error('Invalid age format. Use: 15d (days), 8m (months), 1.6y, 2.3y or 35 (years)');
        return;
      }
    }

    // Phone / Attender flow: pre-register, no token yet
    if (entrySource === 'phone' || entrySource === 'attender') {
      if (!name.trim()) { toast.error('Name required for phone/attender booking'); return; }
      if (entrySource === 'phone' && !contactNumber.trim()) {
        toast.error('Phone number required for phone booking');
        return;
      }
      setLoading(true);
      try {
        const r = await axios.post(
          `${API}/patients/pre-register`,
          {
            name: name.trim(),
            age: age.trim() ? age.trim().toLowerCase() : '',
            sex: sex || '',
            contact_number: contactNumber.trim(),
            place: place.trim(),
            arrival_source: entrySource,
            bp: vitalBp.trim(),
            pr: vitalPr.trim(),
            spo2: vitalSpo2.trim(),
            temp: vitalTemp.trim(),
            weight: vitalWeight.trim(),
            fever,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const prov = r.data?.provisional_id || 'PRE-?';
        toast.success(`${entrySource === 'phone' ? '📞 Phone booking' : '👤 Attender booking'} saved · ${prov}`);
        setName(''); setAge(''); setSex('');
        setUrgent(false); setFever(false); setReview(false); setDiabetes(false);
        setContactNumber(''); setPlace('');
        setVitalBp(''); setVitalPr(''); setVitalSpo2(''); setVitalTemp(''); setVitalWeight('');
        setPhoneLookup(null);
        setSelectedDmEntry(null);
        setDmInlineSearch(''); setDmInlineResults([]);
        await fetchPendingArrival();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save booking');
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/patients`,
        { 
          name: name.trim() || '', 
          age: age.trim() ? age.trim().toLowerCase() : '', 
          sex: sex || '', 
          urgent, 
          fever, 
          review,
          diabetes,
          htn: false,
          contact_number: contactNumber.trim(),
          place: place.trim(),
          bp: vitalBp.trim(),
          pr: vitalPr.trim(),
          spo2: vitalSpo2.trim(),
          temp: vitalTemp.trim(),
          weight: vitalWeight.trim(),
          existing_diabetic_id: selectedDmEntry ? selectedDmEntry.diabetic_id : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newPatient = response.data;
      toast.success(`Token Created: ${newPatient.token_number}`);
      setLastAddedPatient(newPatient);
      setName('');
      setAge('');
      setSex('');
      setUrgent(false);
      setFever(false);
      setReview(false);
      setDiabetes(false);
      setContactNumber('');
      setPlace('');
      setVitalBp('');
      setVitalPr('');
      setVitalSpo2('');
      setVitalTemp('');
      setVitalWeight('');
      setPhoneLookup(null);
      setSelectedDmEntry(null);
      setDmInlineSearch('');
      setDmInlineResults([]);
      fetchQueue();
      fetchNextToken();
      fetchDaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add patient');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintToken = (patient) => {
    const styles = `body { font-family: Arial, sans-serif; text-align: center; padding: 10px; margin: 0; line-height: 1.2; }
      .clinic { font-size: 24px; font-weight: 900; letter-spacing: 0.5px; }
      .clinic-ta { font-size: 15px; font-weight: 800; margin-top: 2px; }
      .phones { font-size: 12px; font-weight: 700; margin-top: 2px; }
      .doctor { font-size: 15px; font-weight: 800; margin-top: 3px; }
      .token { font-size: 72px; font-weight: bold; margin: 4px 0; line-height: 1; }
      .pname { font-size: 22px; font-weight: 800; margin: 2px 0; }
      .pinfo { font-size: 18px; font-weight: 700; margin: 2px 0 4px; }
      .tamil-note { font-size: 15px; font-weight: 700; margin-top: 6px; border-top: 1px dashed #000; padding-top: 5px; }
      .info { font-size: 14px; margin: 3px 0; }
      .date { font-size: 12px; color: #444; }`;
    const body = `<div class="clinic">K.M. MULTI SPECIALITY CLINIC</div>
      <div class="clinic-ta">K.M. சிறப்பு சிகிச்சை மையம், கோலியனூர்</div>
      <div class="phones">Clinic: 86085 95239 &nbsp;|&nbsp; Lab: 93633 24394</div>
      ${onDutyDoctor?.name ? `<div class="doctor">${escapeHtml(onDutyDoctor.name)}${onDutyDoctor.qualification ? `, ${escapeHtml(onDutyDoctor.qualification)}` : ''}</div>` : ''}
      <div class="token">${escapeHtml(patient.token_number)}</div>
      <div class="pname">${escapeHtml(patient.name) || 'Nameless Entry'}</div>
      <div class="pinfo">${escapeHtml(patient.age)} &bull; ${escapeHtml(patient.sex)}</div>
      ${vitalsTableHtml(patient, isPaedsPatient(patient), 11, '4px auto 6px')}
      ${patient.review ? '<div class="info" style="color: purple; font-weight: bold;">REVIEW PATIENT</div>' : ''}
      <div class="date">${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
      <div class="tamil-note">உங்கள் டோக்கன் ரசீதை மருத்துவரிடம் கொடுக்கவும்.</div>`;
    safePrintWindow(`Token - ${patient.token_number}`, `<style>${styles}</style>${body}`);
  };

  const handleUpdateName = async (patientId) => {
    // Allow updating with fields - now includes urgent and fever
    const updates = {};
    if (editingName.trim()) updates.name = editingName.trim();
    if (editingAge.trim()) {
      // Validate age format - supports decimals like 1.6y, 2.3y
      const agePattern = /^(\d+\.?\d*)(d|m|y)?$/i;
      if (!agePattern.test(editingAge.trim())) {
        toast.error('Invalid age format. Use: 15d (days), 8m (months), 1.6y, 2.3y or 35 (years)');
        return;
      }
      updates.age = editingAge.trim().toLowerCase();
    }
    if (editingSex) updates.sex = editingSex;
    
    // Always include urgent and fever flags
    updates.urgent = editingUrgent;
    updates.fever = editingFever;
    Object.assign(updates, editingVitals);

    try {
      await axios.patch(
        `${API}/patients/${patientId}/update`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient updated successfully');
      setEditingPatientId(null);
      setEditingName('');
      setEditingAge('');
      setEditingSex('');
      setEditingUrgent(false);
      setEditingFever(false);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update patient');
    }
  };

  const startEditing = (patient) => {
    setEditingPatientId(patient.id);
    setEditingName(patient.name || '');
    setEditingAge(patient.age || '');
    setEditingSex(patient.sex || '');
    setEditingUrgent(patient.urgent || false);
    setEditingFever(patient.fever || false);
    setEditingVitals({ bp: patient.bp || '', pr: patient.pr || '', spo2: patient.spo2 || '', temp: patient.temp || '', weight: patient.weight || '' });
  };

  const cancelEditing = () => {
    setEditingPatientId(null);
    setEditingName('');
    setEditingAge('');
    setEditingSex('');
    setEditingUrgent(false);
    setEditingFever(false);
  };

  const handleEnterCabin = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/enter-cabin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient sent to cabin');
      fetchQueue();
      fetchDaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleQueueNext = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/queue-next`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient queued as next');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue patient');
    }
  };

  const handleUnqueue = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/unqueue`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient removed from queue');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unqueue patient');
    }
  };

  const handleEnterER = async (patientId, bed = null) => {
    try {
      const url = bed 
        ? `${API}/patients/${patientId}/enter-er?bed=${bed}`
        : `${API}/patients/${patientId}/enter-er`;
      await axios.patch(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Patient sent to ER${bed ? ` - Bed ${bed}` : ''}`);
      setShowERBedModal(false);
      setErPatientId(null);
      setErEditMode(false);
      fetchQueue();
      fetchDaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Update ER bed for patient already in ER
  const handleUpdateERBed = async (patientId, bed) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/update-er-bed`,
        { bed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Bed changed to ${bed}`);
      setShowERBedModal(false);
      setErPatientId(null);
      setErEditMode(false);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bed');
    }
  };

  // Fetch occupied beds
  const fetchOccupiedBeds = async () => {
    try {
      const response = await axios.get(`${API}/er/occupied-beds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOccupiedBeds(response.data.occupied_beds || {});
    } catch (error) {
      console.error('Error fetching occupied beds:', error);
    }
  };

  // Open ER bed selection modal
  const openERBedModal = (patientId, isEdit = false) => {
    setErPatientId(patientId);
    setErEditMode(isEdit);
    fetchOccupiedBeds(); // Fetch current occupancy
    setShowERBedModal(true);
  };

  const handleCloseConsultation = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Consultation closed successfully');
      fetchQueue();
      fetchDaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close consultation');
    }
  };

  const handleBackToWaiting = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/back-to-waiting`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient sent back to waiting');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDeletePatient = async (patientId, tokenNum) => {
    if (!window.confirm(`Delete patient ${tokenNum}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${tokenNum}`);
      fetchQueue();
      fetchNextToken();
      fetchDaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete patient');
    }
  };

  const waitingPatients = patients.filter(p => p.status === 'waiting');
  const inCabinPatients = patients.filter(p => p.status === 'in_cabin');
  const inERPatients = patients.filter(p => p.status === 'in_er');
  const completedPatients = patients.filter(p => p.status === 'completed');

  // Helper function to parse age string to years
  const parseAgeToYears = (ageStr) => {
    if (!ageStr) return 0;
    const age = String(ageStr).trim().toLowerCase();
    if (age.endsWith('d')) {
      const days = parseInt(age.slice(0, -1));
      return isNaN(days) ? 0 : days / 365;
    } else if (age.endsWith('m')) {
      const months = parseInt(age.slice(0, -1));
      return isNaN(months) ? 0 : months / 12;
    } else if (age.endsWith('y')) {
      const years = parseInt(age.slice(0, -1));
      return isNaN(years) ? 0 : years;
    } else {
      const years = parseInt(age);
      return isNaN(years) ? 0 : years;
    }
  };

  // Helper function to get age days
  const parseAgeToDays = (ageStr) => {
    if (!ageStr) return 0;
    const age = String(ageStr).trim().toLowerCase();
    if (age.endsWith('d')) {
      return parseInt(age.slice(0, -1)) || 0;
    } else if (age.endsWith('m')) {
      return (parseInt(age.slice(0, -1)) || 0) * 30;
    } else if (age.endsWith('y')) {
      return (parseInt(age.slice(0, -1)) || 0) * 365;
    } else {
      return (parseInt(age) || 0) * 365;
    }
  };

  // Get age symbol based on age category
  const getAgeSymbol = (ageStr) => {
    const days = parseAgeToDays(ageStr);
    const years = parseAgeToYears(ageStr);
    
    if (days < 28) return { symbol: '👶', label: 'Newborn', color: 'text-pink-600' };
    if (years < 1) return { symbol: '🍼', label: 'Infant', color: 'text-blue-500' };
    if (years < 3) return { symbol: '🧒', label: 'Toddler', color: 'text-green-500' };
    if (years < 12) return { symbol: '👦', label: 'Child', color: 'text-orange-500' };
    return null; // Adults don't get symbol
  };

  // Calculate average waiting time for patients still waiting
  const getAverageWaitingTime = () => {
    const waitingPts = patients.filter(p => p.status === 'waiting' || p.status === 'queued_next');
    if (waitingPts.length === 0) return null;
    
    const now = Date.now();
    let totalWaitMins = 0;
    
    waitingPts.forEach(p => {
      if (p.created_at) {
        const created = new Date(p.created_at).getTime();
        const waitMins = Math.floor((now - created) / (1000 * 60));
        totalWaitMins += waitMins;
      }
    });
    
    const avgMins = Math.round(totalWaitMins / waitingPts.length);
    return {
      avgMins,
      count: waitingPts.length,
      formatted: avgMins < 60 ? `${avgMins}m` : `${Math.floor(avgMins / 60)}h ${avgMins % 60}m`,
      color: avgMins <= 30 ? 'green' : avgMins <= 60 ? 'yellow' : 'red'
    };
  };

  const avgWaitingTime = getAverageWaitingTime();

  // Filter patients based on search query
  const tokenSortKey = (p) => {
    const t = (p.token_number || '').toUpperCase();
    const m = t.match(/^([A-Z]+)-?(\d+)$/);
    if (!m) return [9, 9999];
    // Prefix priority: A (and legacy adult G/W/O) before P, then anything else
    const prefix = m[1];
    let group;
    if (['A', 'G', 'W', 'O'].includes(prefix)) group = 0;
    else if (prefix === 'P') group = 1;
    else group = 2;
    return [group, parseInt(m[2], 10)];
  };

  const filteredPatients = patients.filter(patient => {
    // First apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        (patient.name || '').toLowerCase().includes(query) ||
        patient.token_number.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    
    // Then apply tab filter — by token series (W/O/P)
    if (activeTab === 'all') return true;
    return getPatientSeries(patient) === activeTab;
  }).sort((a, b) => {
    const [ag, an] = tokenSortKey(a);
    const [bg, bn] = tokenSortKey(b);
    return ag - bg || an - bn;
  });

  // Count patients by category (age-based, used elsewhere) + series (used by tabs)
  const categoryCounts = {
    all: patients.length,
    infants: patients.filter(p => getPatientCategory(p) === 'infants').length,
    children: patients.filter(p => getPatientCategory(p) === 'children').length,
    adults: patients.filter(p => getPatientCategory(p) === 'adults').length,
    adult: patients.filter(p => getPatientSeries(p) === 'adult').length,
    paeds: patients.filter(p => getPatientSeries(p) === 'paeds').length,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'in_cabin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'queued_next': return 'bg-purple-100 text-purple-800 border-purple-300 animate-pulse';
      case 'in_er': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 ${isFlashing ? 'flash-screen' : ''}`} onClick={() => showPlaceDropdown && setShowPlaceDropdown(false)}>
      {/* Doctor Bell Alert Overlay */}
      {activeBell && (
        <div
          className="fixed inset-x-0 top-0 z-[120] flex justify-center pointer-events-none p-2 md:p-3"
          data-testid="staff-bell-banner"
          data-bell-type={activeBell.type}
        >
          <div
            className="pointer-events-auto w-full max-w-2xl rounded-xl shadow-2xl border-4 px-4 py-3 flex items-center gap-3 animate-pulse"
            style={{
              background: activeBell.type === 'cbg'
                ? 'linear-gradient(135deg,#fde047,#f59e0b)'
                : activeBell.type === 'temp'
                ? 'linear-gradient(135deg,#fdba74,#dc2626)'
                : 'linear-gradient(135deg,#fca5a5,#7f1d1d)',
              borderColor: '#fef3c7',
              color: 'white',
            }}
          >
            <div className="text-4xl md:text-5xl drop-shadow">{BELL_ICON[activeBell.type] || '🛎️'}</div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-xl md:text-2xl tracking-wide uppercase drop-shadow" data-testid="staff-bell-label">
                Doctor is calling — {BELL_LABEL[activeBell.type] || 'Attention'}
              </div>
              <div className="text-xs md:text-sm opacity-90 mt-0.5">
                {activeBell.note ? `Note: ${activeBell.note} · ` : ''}rung at {new Date(activeBell.rung_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            </div>
            <button
              onClick={acknowledgeBell}
              className="ml-auto bg-white text-slate-900 hover:bg-slate-100 font-bold px-4 md:px-6 py-2 md:py-3 rounded-lg shadow text-sm md:text-base whitespace-nowrap"
              data-testid="staff-bell-ack"
            >
              Acknowledge
            </button>
          </div>
        </div>
      )}
      {/* Header - Modern Blue Theme */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-2 md:gap-3">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  K.M
                </h1>
                <span className="text-xs md:text-sm font-medium text-blue-100">Staff Dashboard</span>
              </div>
              <p className="text-xs text-blue-200 mt-0.5">Multi-Speciality Clinic, Koliyanur</p>
              <LiveClock className="text-xs text-blue-200 mt-0.5" />
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              {/* Essential buttons always visible */}
              <Button onClick={() => setSoundEnabled(!soundEnabled)} variant="ghost" size="icon"
                data-testid="sound-toggle-button"
                className={`hover:bg-blue-500/30 text-white ${soundEnabled ? 'text-green-300' : 'text-blue-200'}`}>
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
              
              {/* Mobile: hamburger toggle */}
              <Button variant="ghost" onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
                className="md:hidden text-white hover:bg-blue-500/30" data-testid="staff-menu-toggle">
                {headerMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>

              {/* Menu items: always on desktop, toggled on mobile */}
              <div className={`${headerMenuOpen ? 'flex' : 'hidden md:flex'} items-center gap-1 md:gap-2 flex-wrap`}>
              <Button onClick={() => { setShowDiabeticRegistry(true); fetchDiabeticRegistry(); }} variant="ghost"
                data-testid="diabetic-registry-button" className="hover:bg-blue-500/30 text-white text-xs md:text-sm">
                <Activity className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">DM Registry</span>
              </Button>
              <Button onClick={() => { setShowCampEntry(!showCampEntry); if (!showCampEntry) fetchCampPatients(campFilterType, campStartDate, campEndDate); }} variant="ghost"
                data-testid="camp-entry-button" className="hover:bg-green-500/30 text-white text-xs md:text-sm">
                <Users className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">Camp</span>
              </Button>
              <Button onClick={() => { setShowReviews(true); fetchReviews(); }} variant="ghost"
                data-testid="review-reminders-button" className="hover:bg-orange-500/30 text-white text-xs md:text-sm">
                <Calendar className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">Reviews</span>
              </Button>
              <Button onClick={openPastRecords} variant="ghost"
                data-testid="past-records-button" className="hover:bg-blue-500/30 text-white text-xs md:text-sm">
                <History className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">History</span>
              </Button>
              <Button onClick={() => { setShowDeleteDay(true); fetchDeleteDaySummary(); }} variant="ghost"
                data-testid="delete-day-button" className="hover:bg-red-500/30 text-white text-xs md:text-sm">
                <Trash2 className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">Delete Day</span>
              </Button>
              <Button onClick={() => { setShowPlacesManager(true); setPlacesUnlocked(false); setPlacesPassword(''); fetchPlacesList(); }} variant="ghost"
                data-testid="places-manager-button" className="hover:bg-teal-500/30 text-white text-xs md:text-sm">
                <MapPin className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">Places</span>
              </Button>
              <Button onClick={() => setShowSettings(true)} variant="ghost"
                data-testid="settings-button" className="hover:bg-blue-500/30 text-white text-xs md:text-sm">
                <Settings className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">Doctors</span>
              </Button>
              <Button onClick={logout} variant="ghost"
                data-testid="logout-button" className="hover:bg-red-500/30 text-white text-xs md:text-sm">
                <LogOut className="mr-1 md:mr-2 h-4 w-4" /><span className="hidden md:inline">Logout</span>
              </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Current Patient - STICKY elegant compact bar */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-slate-50 via-white to-blue-50 shadow-md border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-2">
          {currentPatient ? (
            <div className={`${isFlashing ? 'pulse-card' : ''}`} data-testid="staff-cabin-sticky">
              {/* Row 1: In-Cabin info + Action Buttons (always together) */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="bg-gradient-to-br from-blue-600 to-blue-700 text-white text-[10px] md:text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded-md shadow-sm flex-shrink-0">In Cabin</span>
                  <span
                    className="text-2xl md:text-3xl font-black text-blue-700 leading-none flex-shrink-0 tracking-tight"
                    style={{ fontFamily: 'Arial Black, sans-serif' }}
                  >
                    {currentPatient.token_number}
                  </span>
                  <span className="text-base md:text-lg font-semibold text-slate-800 truncate">
                    {currentPatient.name || 'Unknown'}
                  </span>
                  <span className="text-sm text-slate-500 hidden md:inline flex-shrink-0">
                    · {currentPatient.age}{currentPatient.sex ? ` · ${currentPatient.sex}` : ''}
                  </span>
                  {currentPatient.urgent && (
                    <span className="flex items-center text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full flex-shrink-0 shadow-sm">
                      <AlertCircle className="h-3 w-3 mr-0.5" /> URG
                    </span>
                  )}
                  {currentPatient.fever && (
                    <span className="flex items-center text-[10px] font-bold text-white bg-orange-500 px-2 py-0.5 rounded-full flex-shrink-0 shadow-sm">
                      <Thermometer className="h-3 w-3 mr-0.5" /> FEV
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    onClick={() => handleBackToWaiting(currentPatient.id)}
                    variant="outline"
                    size="sm"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 font-medium h-10 w-10 md:w-auto md:px-3 p-0 rounded-lg transition-colors"
                    data-testid="change-patient-staff-button"
                    title="Send back to waiting & call another patient"
                  >
                    <Undo2 className="h-4 w-4" />
                    <span className="hidden md:inline ml-1.5 text-sm">Change</span>
                  </Button>
                  <Button
                    onClick={() => handleCloseConsultation(currentPatient.id)}
                    className="bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold h-10 md:h-11 px-5 md:px-7 text-base md:text-base rounded-lg shadow-md hover:shadow-lg transition-all"
                    data-testid="close-consultation-staff-button"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Close
                  </Button>
                </div>
              </div>

              {/* Row 2: Queue pills (left) + Auto-call/Paed/Fever (right) */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {patients.filter(p => p.status === 'queued_next').length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {patients
                      .filter(p => p.status === 'queued_next')
                      .sort((a, b) => (a.queued_at || '').localeCompare(b.queued_at || ''))
                      .map((qp, idx) => (
                        <span key={qp.id} className="inline-flex items-center gap-1.5 bg-white border border-purple-200 rounded-full pl-2 pr-1 py-1 shadow-sm">
                          <FastForward className="h-3.5 w-3.5 text-purple-500" />
                          <span className="font-semibold text-purple-600 text-xs uppercase tracking-wide">{idx === 0 ? 'Next' : 'Then'}</span>
                          <span className="font-bold text-purple-800 text-sm tabular-nums">{qp.token_number}</span>
                          <span className="text-purple-700 truncate max-w-[90px] text-sm font-medium">{qp.name || 'Nameless'}</span>
                          <button
                            onClick={() => handleUnqueue(qp.id)}
                            className="text-purple-300 hover:text-purple-700 hover:bg-purple-50 rounded-full p-0.5 transition-colors"
                            title="Cancel"
                            data-testid={`unqueue-preview-${qp.id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <Button
                    onClick={() => setAutoCallEnabled(!autoCallEnabled)}
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 rounded-full transition-colors ${autoCallEnabled ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}
                    title={`Auto-Call ${autoCallEnabled ? 'ON' : 'OFF'}`}
                    data-testid="auto-call-toggle"
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  {autoCallEnabled && !currentPatient && (
                    <Button
                      onClick={handleAutoCallNext}
                      disabled={autoCallLoading}
                      size="sm"
                      className="h-8 w-8 p-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                      title="Call Next Patient"
                      data-testid="auto-call-next-button"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  )}
                  <span className="inline-flex items-center bg-pink-50 border border-pink-200 px-2.5 py-1 rounded-md font-semibold text-pink-700 text-sm" title="Paediatric (under 12)">
                    Paed <span className="ml-1 font-bold tabular-nums">{(categoryCounts.infants || 0) + (categoryCounts.children || 0)}</span>
                  </span>
                  <span className="inline-flex items-center bg-violet-50 border border-violet-200 px-2.5 py-1 rounded-md font-semibold text-violet-700 text-sm" title="Adults (12+)">
                    Adult <span className="ml-1 font-bold tabular-nums">{categoryCounts.adults || 0}</span>
                  </span>
                  <span className="inline-flex items-center bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-md font-semibold text-orange-700 text-sm" title="Active fever cases">
                    Fever <span className="ml-1 font-bold tabular-nums">{patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next' || p.status === 'in_cabin')).length}</span>
                  </span>
                  <button
                    onClick={openRxAnchorModal}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-semibold text-sm border transition-colors ${
                      rxAnchorInfo?.next_to_assign
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                        : 'bg-rose-50 border-rose-300 text-rose-700 border-dashed hover:bg-rose-100 animate-pulse'
                    }`}
                    title={rxAnchorInfo?.next_to_assign ? `Today's next Rx will be ${rxAnchorInfo.next_to_assign} — click to edit` : "Set today's Rx start"}
                    data-testid="staff-rx-anchor-btn"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {rxAnchorInfo?.next_to_assign ? `Next Rx ${rxAnchorInfo.next_to_assign}` : 'Set Rx'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 py-1">
              <div className="flex items-center gap-2 text-slate-500" data-testid="staff-cabin-empty">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-medium">No patient in cabin</span>
                <span className="text-xs text-slate-400 hidden md:inline">— Waiting for next call</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                  onClick={() => setAutoCallEnabled(!autoCallEnabled)}
                  variant="ghost"
                  size="sm"
                  className={`h-8 w-8 p-0 rounded-full ${autoCallEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                  title={`Auto-Call ${autoCallEnabled ? 'ON' : 'OFF'}`}
                  data-testid="auto-call-toggle"
                >
                  <Power className="h-4 w-4" />
                </Button>
                {autoCallEnabled && (
                  <Button
                    onClick={handleAutoCallNext}
                    disabled={autoCallLoading}
                    size="sm"
                    className="h-8 w-8 p-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                    title="Call Next Patient"
                    data-testid="auto-call-next-button"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                )}
                <span className="inline-flex items-center bg-pink-50 border border-pink-200 px-2.5 py-1 rounded-md font-semibold text-pink-700 text-sm">
                  Paed <span className="ml-1 font-bold tabular-nums">{(categoryCounts.infants || 0) + (categoryCounts.children || 0)}</span>
                </span>
                <span className="inline-flex items-center bg-violet-50 border border-violet-200 px-2.5 py-1 rounded-md font-semibold text-violet-700 text-sm">
                  Adult <span className="ml-1 font-bold tabular-nums">{categoryCounts.adults || 0}</span>
                </span>
                <span className="inline-flex items-center bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-md font-semibold text-orange-700 text-sm">
                  Fever <span className="ml-1 font-bold tabular-nums">{patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next' || p.status === 'in_cabin')).length}</span>
                </span>
                <button
                  onClick={openRxAnchorModal}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-semibold text-sm border transition-colors ${
                    rxAnchorInfo?.next_to_assign
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                      : 'bg-rose-50 border-rose-300 text-rose-700 border-dashed hover:bg-rose-100 animate-pulse'
                  }`}
                  title={rxAnchorInfo?.next_to_assign ? `Today's next Rx will be ${rxAnchorInfo.next_to_assign} — click to edit` : "Set today's Rx start"}
                  data-testid="staff-rx-anchor-btn-2"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {rxAnchorInfo?.next_to_assign ? `Next Rx ${rxAnchorInfo.next_to_assign}` : 'Set Rx'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto p-4 md:p-6 pt-2 md:pt-6">
        {/* Stats - compact inline on mobile */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          <div className="flex items-center gap-1.5 bg-white rounded-lg border px-3 py-2 shadow-sm min-w-fit">
            <span className="text-lg font-black text-primary">{patients.length}</span>
            <span className="text-xs text-muted-foreground">Total</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white rounded-lg border px-3 py-2 shadow-sm min-w-fit">
            <span className="text-lg font-black text-amber-600">{waitingPatients.length}</span>
            <span className="text-xs text-muted-foreground">Waiting</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white rounded-lg border px-3 py-2 shadow-sm min-w-fit">
            <span className="text-lg font-black text-blue-600">{inCabinPatients.length}</span>
            <span className="text-xs text-muted-foreground">Cabin</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white rounded-lg border px-3 py-2 shadow-sm min-w-fit">
            <span className="text-lg font-black text-green-600">{completedPatients.length}</span>
            <span className="text-xs text-muted-foreground">Done</span>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Left Column - New Token Entry - COMPACT */}
          <div className="lg:col-span-4">
            <Card className="p-4 border-border/60 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <UserPlus className="h-4 w-4 mr-2 text-accent" />
                  <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Add Patient
                  </h2>
                </div>
              </div>

              {/* Unified Entry Source Picker — Walk-in / Phone / Attender */}
              <div className="grid grid-cols-3 gap-1 mb-3 p-1 bg-slate-100 rounded-lg" data-testid="entry-source-picker">
                {[
                  { key: 'walk_in', label: 'Walk-in', icon: '🚶', active: 'bg-emerald-600 text-white', hint: 'Generate token now' },
                  { key: 'phone',   label: 'Phone',   icon: '📞', active: 'bg-blue-600 text-white',    hint: 'Pre-register · token on arrival' },
                  { key: 'attender',label: 'Attender',icon: '👤', active: 'bg-amber-600 text-white',   hint: 'Print slip · token on arrival' },
                ].map(({ key, label, icon, active, hint }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setEntrySource(key)}
                    title={hint}
                    data-testid={`entry-source-${key}`}
                    className={`flex flex-col items-center justify-center py-1.5 rounded-md text-[11px] font-bold transition-all ${entrySource === key ? `${active} shadow` : 'text-slate-600 hover:bg-white'}`}
                  >
                    <span className="text-base leading-none">{icon}</span>
                    <span className="mt-0.5">{label}</span>
                  </button>
                ))}
              </div>

              <form onSubmit={handleAddPatient} className="space-y-3">
                {/* Adult (A) + Paeds (P) Token Display — only for Walk-in */}
                {entrySource === 'walk_in' && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center py-2 bg-violet-50 rounded-lg border border-violet-200">
                    <div className="text-xs text-violet-600 mb-0.5">Adult (12+)</div>
                    <div
                      className="text-2xl font-bold tracking-tighter text-violet-700"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      data-testid="next-token-adult"
                    >
                      {nextTokenGeneral}
                    </div>
                  </div>
                  <div className="text-center py-2 bg-pink-50 rounded-lg border border-pink-200">
                    <div className="text-xs text-pink-600 mb-0.5">Paediatric (&lt;12)</div>
                    <div
                      className="text-2xl font-bold tracking-tighter text-pink-700"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      data-testid="next-token-paediatric"
                    >
                      {nextTokenPaediatric}
                    </div>
                  </div>
                </div>
                )}

                {/* Banner for non-walk-in modes */}
                {entrySource !== 'walk_in' && (
                  <div
                    className={`text-[11px] font-medium rounded-md border px-2.5 py-1.5 flex items-center gap-1.5 ${entrySource === 'phone' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                    data-testid="entry-source-banner"
                  >
                    <span className="font-bold">
                      {entrySource === 'phone' ? '📞 Phone Booking' : '👤 Attender Booking'}
                    </span>
                    <span className="opacity-75">— token is issued immediately</span>
                  </div>
                )}

                <div className="space-y-1">
                  <Label htmlFor="patient-name" className="text-xs">
                    Patient Name {entrySource === 'walk_in' ? '(Optional)' : <span className="text-red-600 font-bold">*</span>}
                  </Label>
                  <Input
                    id="patient-name"
                    data-testid="patient-name-input"
                    type="text"
                    placeholder="Leave empty for quick token"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="patient-age" className="text-xs">Age</Label>
                    <Input
                      id="patient-age"
                      data-testid="patient-age-input"
                      type="text"
                      placeholder="e.g., 15d, 8m, 35"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="patient-sex" className="text-xs">Sex</Label>
                    <select
                      id="patient-sex"
                      data-testid="patient-sex-select"
                      value={sex}
                      onChange={(e) => setSex(e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring/20"
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Vitals — captured at desk for all entry modes */}
                <div className="grid grid-cols-6 gap-1.5 pt-1">
                  <div className="space-y-1 col-span-2">
                    <Label htmlFor="vital-bp" className="text-xs">BP <span className="text-[10px] text-muted-foreground">mmHg</span></Label>
                    <Input id="vital-bp" data-testid="vital-bp-input" type="text" inputMode="decimal" placeholder="120/80" value={vitalBp} onChange={(e) => setVitalBp(formatBp(e.target.value))} className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="vital-pr" className="text-xs">PR <span className="text-[10px] text-muted-foreground">/min</span></Label>
                    <Input id="vital-pr" data-testid="vital-pr-input" type="text" inputMode="numeric" placeholder="72" value={vitalPr} onChange={(e) => setVitalPr(e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="vital-spo2" className="text-xs">SpO₂ <span className="text-[10px] text-muted-foreground">%</span></Label>
                    <Input id="vital-spo2" data-testid="vital-spo2-input" type="text" inputMode="numeric" placeholder="98" value={vitalSpo2} onChange={(e) => setVitalSpo2(e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="vital-temp" className="text-xs">Temp <span className="text-[10px] text-muted-foreground">°F</span></Label>
                    <Input id="vital-temp" data-testid="vital-temp-input" type="text" inputMode="decimal" placeholder="98.6" value={vitalTemp} onChange={(e) => { setVitalTemp(e.target.value); if (parseFloat(e.target.value) > 100) setFever(true); }} className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="vital-weight" className="text-xs">Wt <span className="text-[10px] text-muted-foreground">kg</span></Label>
                    <Input id="vital-weight" data-testid="vital-weight-input" type="text" inputMode="decimal" placeholder="12.5" value={vitalWeight} onChange={(e) => setVitalWeight(e.target.value.replace(/[^0-9.]/g, '').replace(/^(\d*\.\d?).*$/, '$1'))} className="h-9 text-sm" />
                  </div>
                </div>

                {/* Compact Checkboxes - 2x2: ER, Fever, DM, Review */}
                <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 pt-1">
                  <div className="flex items-center space-x-1.5">
                    <Checkbox id="urgent" data-testid="urgent-checkbox" checked={urgent} onCheckedChange={setUrgent} className="h-4 w-4" />
                    <Label htmlFor="urgent" className="text-xs font-medium cursor-pointer text-red-600">ER</Label>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Checkbox id="fever" data-testid="fever-checkbox" checked={fever} onCheckedChange={setFever} className="h-4 w-4" />
                    <Label htmlFor="fever" className="text-xs font-medium cursor-pointer text-orange-600">Fever</Label>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Checkbox id="diabetes" data-testid="diabetes-checkbox" checked={diabetes} onCheckedChange={setDiabetes} className="h-4 w-4" />
                    <Label htmlFor="diabetes" className="text-xs font-medium cursor-pointer text-purple-600">DM</Label>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Checkbox id="review" data-testid="review-checkbox" checked={review} onCheckedChange={setReview} className="h-4 w-4" />
                    <Label htmlFor="review" className="text-xs font-medium cursor-pointer text-indigo-600">Review</Label>
                  </div>
                </div>

                {/* Diabetic Patient - Search existing DM entry */}
                {diabetes && (
                  <div className="p-2 bg-purple-50 rounded-lg border border-purple-200 space-y-2">
                    {selectedDmEntry ? (
                      <div className="flex items-center justify-between bg-purple-100 rounded-lg p-2 border border-purple-300">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-purple-800 text-sm bg-purple-200 px-2 py-0.5 rounded">{selectedDmEntry.diabetic_id}</span>
                          <div>
                            <span className="font-bold text-purple-900 text-sm">{selectedDmEntry.name}</span>
                            <div className="text-xs text-purple-600">{selectedDmEntry.visit_dates?.length || 0} prev visits</div>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:bg-red-50" onClick={clearDmSelection} data-testid="clear-dm-selection">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <label className="text-xs font-bold text-purple-700 block">Search existing DM patient (ID / Phone / Name)</label>
                        <Input
                          type="text"
                          value={dmInlineSearch}
                          onChange={(e) => searchDmInline(e.target.value)}
                          placeholder="Search by DM ID, phone, or name..."
                          className="h-8 text-sm"
                          data-testid="dm-inline-search"
                        />
                        {dmInlineLoading && <div className="text-xs text-purple-500 py-1">Searching...</div>}
                        {dmInlineResults.length > 0 && (
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {dmInlineResults.map(dm => (
                              <div
                                key={dm.diabetic_id}
                                onClick={() => selectDmEntry(dm)}
                                className="flex items-center justify-between p-2 bg-white rounded border cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                                data-testid={`dm-inline-result-${dm.diabetic_id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-black text-purple-800 text-xs bg-purple-100 px-1.5 py-0.5 rounded">{dm.diabetic_id}</span>
                                  <span className="font-bold text-gray-800 text-xs">{dm.name}</span>
                                </div>
                                <span className="text-xs text-purple-600">{dm.contact_number || ''}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {dmInlineSearch && !dmInlineLoading && dmInlineResults.length === 0 && (
                          <div className="text-xs text-gray-500 py-1">No match — will register as new DM patient</div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Phone Number & Place - Always visible */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Phone No.</Label>
                    <div className="flex gap-1">
                      <Input
                        type="tel"
                        value={contactNumber}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="Phone number"
                        className="h-8 text-sm flex-1"
                        data-testid="contact-number-input"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchPhoneLookup(); } }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                        onClick={searchPhoneLookup}
                        disabled={phoneLookupLoading}
                        data-testid="phone-search-button"
                      >
                        <Search className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1 relative" onClick={(e) => e.stopPropagation()}>
                    <Label className="text-xs">Place</Label>
                    <div className="relative">
                      <input
                        type="text"
                        value={place}
                        onChange={(e) => { setPlace(e.target.value); setPlaceFilter(e.target.value); setShowPlaceDropdown(true); }}
                        onFocus={() => setShowPlaceDropdown(true)}
                        placeholder="Select village..."
                        className="h-8 w-full rounded-md border border-input bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring/20"
                        data-testid="place-input"
                        autoComplete="off"
                      />
                      {showPlaceDropdown && (
                        <div className="absolute z-50 top-9 left-0 right-0 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {placesList
                            .filter(p => !placeFilter || p.toLowerCase().includes(placeFilter.toLowerCase()))
                            .map(p => (
                              <div
                                key={p}
                                onClick={() => selectPlace(p)}
                                className="px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50 transition-colors"
                                data-testid={`place-option-${p}`}
                              >
                                {p}
                              </div>
                            ))
                          }
                          <div
                            onClick={() => selectPlace('Other')}
                            className="px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 text-gray-500 border-t"
                          >
                            Other
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phone Lookup - Family Members (click to select) */}
                {phoneLookupLoading && <div className="text-xs text-blue-500">Looking up phone...</div>}
                {phoneLookup && phoneLookup.family.length > 0 && (
                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-200 space-y-1">
                    <div className="text-xs font-bold text-blue-700">{phoneLookup.family.length} member{phoneLookup.family.length > 1 ? 's' : ''} — tap to select</div>
                    <div className="max-h-28 overflow-y-auto space-y-0.5">
                      {phoneLookup.family.map((f, i) => (
                        <div
                          key={`${f.name}-${i}`}
                          onClick={() => selectFamilyMember(f)}
                          className="flex items-center justify-between text-xs bg-white px-2 py-1.5 rounded border cursor-pointer hover:bg-blue-100 hover:border-blue-400 transition-colors"
                          data-testid={`family-member-${i}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-800">{f.name}</span>
                            <span className="text-slate-500">{f.age} {f.sex}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {f.place && <span className="text-slate-400">{f.place}</span>}
                            <span className="text-blue-500 font-bold">{f.visits} visit{f.visits > 1 ? 's' : ''}</span>
                            <span className="text-slate-400">Last: {f.last_visit}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="add-patient-button"
                  className={`w-full h-9 text-sm font-semibold ${
                    entrySource === 'walk_in' ? 'bg-emerald-600 hover:bg-emerald-700' :
                    entrySource === 'phone'   ? 'bg-blue-600 hover:bg-blue-700' :
                                                'bg-amber-600 hover:bg-amber-700'
                  } text-white`}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (
                    entrySource === 'walk_in' ? '🚶 Create Token' :
                    entrySource === 'phone'   ? '📞 Save Phone Booking' :
                                                '👤 Save Attender Booking'
                  )}
                </Button>

                {/* Print Token Section - shown after adding patient */}
                {lastAddedPatient && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-green-800">Token Created!</p>
                        <p className="text-xl font-bold text-green-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {lastAddedPatient.token_number}
                        </p>
                        <p className="text-xs text-green-600">{lastAddedPatient.name || 'Nameless Entry'}</p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => handlePrintToken(lastAddedPatient)}
                        variant="outline"
                        className="border-green-500 text-green-700 hover:bg-green-100"
                        data-testid="print-token-button"
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print Token
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </Card>
          </div>

          {/* Right Column - Live Queue */}
          <div className="lg:col-span-8">
            <Card className="p-6 border-border/60 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-2 text-accent" />
                  <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Today&apos;s Queue
                  </h2>
                </div>
                <Input
                  type="text"
                  placeholder="Search by name or token..."
                  data-testid="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-xs h-10"
                />
              </div>

              {/* Queue Tabs — Adult / Paeds */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button
                  variant={activeTab === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('all')}
                  className="text-xs"
                  data-testid="series-tab-all"
                >
                  All ({categoryCounts.all})
                </Button>
                <Button
                  variant={activeTab === 'adult' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('adult')}
                  className={`text-xs ${activeTab === 'adult' ? 'bg-violet-600 hover:bg-violet-700' : 'text-violet-600 border-violet-300'}`}
                  data-testid="series-tab-adult"
                >
                  <User className="h-3 w-3 mr-1" />
                  Adult ({categoryCounts.adult})
                </Button>
                <Button
                  variant={activeTab === 'paeds' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('paeds')}
                  className={`text-xs ${activeTab === 'paeds' ? 'bg-pink-600 hover:bg-pink-700' : 'text-pink-600 border-pink-300'}`}
                  data-testid="series-tab-paeds"
                >
                  <Baby className="h-3 w-3 mr-1" />
                  Paeds ({categoryCounts.paeds})
                </Button>
              </div>

              {filteredPatients.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{searchQuery ? 'No patients found matching your search' : 'No patients yet today'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Waiting/Active Patients Section */}
                  {filteredPatients.filter(p => p.status === 'waiting' || p.status === 'in_cabin' || p.status === 'in_er').length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Waiting / Active ({filteredPatients.filter(p => p.status === 'waiting' || p.status === 'in_cabin' || p.status === 'in_er').length})
                      </h3>
                      {filteredPatients.filter(p => p.status === 'waiting' || p.status === 'in_cabin' || p.status === 'in_er').map((patient) => (
                        <div
                          key={patient.id}
                          data-testid={`patient-card-${patient.id}`}
                          className={`p-3 md:p-4 bg-white border-2 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 ${
                            patient.urgent ? 'border-red-300 bg-red-50/50' : 
                            patient.review ? 'border-purple-300 bg-purple-50/50' : 
                            'border-blue-100 hover:border-blue-300'
                          }`}
                        >
                          {/* Mobile: Stacked Layout */}
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            {/* Token & Info Section */}
                            <div className="flex items-start gap-3">
                              {/* Token Number - Large & Bold */}
                              <div className={`flex flex-col items-center justify-center min-w-[80px] md:min-w-[100px] p-2 md:p-3 rounded-xl ${
                                patient.urgent ? 'bg-red-500 text-white' :
                                patient.status === 'in_er' ? 'bg-red-500 text-white' :
                                patient.status === 'in_cabin' ? 'bg-blue-500 text-white' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                <span
                                  className="text-xl md:text-2xl font-black tracking-tight"
                                  style={{ fontFamily: 'Arial Black, sans-serif' }}
                                  data-testid={`token-${patient.id}`}
                                >
                                  {patient.token_number}
                                </span>
                                {patient.urgent && (
                                  <span className="text-xs font-bold mt-0.5">URGENT</span>
                                )}
                              </div>
                              
                              {/* Patient Details */}
                              <div className="flex-1 min-w-0">
                                {editingPatientId === patient.id ? (
                                  <div className="space-y-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Input
                                        type="text"
                                        value={editingName}
                                        onChange={(e) => setEditingName(e.target.value)}
                                        placeholder="Name"
                                        className="h-9 text-sm flex-1 min-w-[120px]"
                                      />
                                      <Input
                                        type="text"
                                        value={editingAge}
                                        onChange={(e) => setEditingAge(e.target.value)}
                                        placeholder="Age"
                                        className="h-9 text-sm w-20"
                                      />
                                      <select
                                        value={editingSex}
                                        onChange={(e) => setEditingSex(e.target.value)}
                                        className="h-9 w-24 rounded-lg border border-input bg-white px-2 text-sm"
                                      >
                                        <option value="">Sex</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                      </select>
                                    </div>
                                    <VitalsEditGrid values={editingVitals} onChange={setEditingVitals} onFever={() => setEditingFever(true)} testId={`edit-vitals-${patient.id}`} />
                                    <div className="flex items-center gap-4">
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <Checkbox
                                          checked={editingUrgent}
                                          onCheckedChange={(checked) => setEditingUrgent(checked)}
                                          className="border-red-400 data-[state=checked]:bg-red-500"
                                        />
                                        <span className="text-xs font-bold text-red-600">Urgent (ER)</span>
                                      </label>
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <Checkbox
                                          checked={editingFever}
                                          onCheckedChange={(checked) => setEditingFever(checked)}
                                          className="border-orange-400 data-[state=checked]:bg-orange-500"
                                        />
                                        <span className="text-xs font-bold text-orange-600">Fever</span>
                                      </label>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" data-testid={`edit-save-${patient.id}`} className="h-8 text-xs bg-green-500 hover:bg-green-600 text-white rounded-lg" onClick={() => handleUpdateName(patient.id)}>
                                        <Check className="h-3 w-3 mr-1" /> Save
                                      </Button>
                                      <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-300 rounded-lg" onClick={cancelEditing}>
                                        <X className="h-3 w-3 mr-1" /> Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-bold text-base md:text-lg text-slate-800" data-testid={`name-${patient.id}`}>
                                        {patient.name || <span className="text-slate-400 italic">No Name</span>}
                                      </span>
                                      <SourceBadge patient={patient} className="text-[10px] px-1.5" />
                                      {patient.status !== 'completed' && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          data-testid={`edit-patient-${patient.id}`}
                                          className="h-7 px-2 text-blue-600 hover:bg-blue-100 text-xs rounded-lg"
                                          onClick={() => startEditing(patient)}
                                          title="Edit patient details"
                                        >
                                          <Edit2 className="h-3 w-3 mr-1" /> Edit
                                        </Button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                                      {patient.age && getAgeSymbol(patient.age) && (
                                        <span className={`${getAgeSymbol(patient.age).color}`} title={getAgeSymbol(patient.age).label}>
                                          {getAgeSymbol(patient.age).symbol}
                                        </span>
                                      )}
                                      <span className="font-medium">
                                        {patient.age || <span className="italic text-slate-400">No age</span>}
                                        {' • '}
                                        {patient.sex || <span className="italic text-slate-400">No sex</span>}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {patient.fever && (
                                        <span className="flex items-center text-xs font-bold text-white bg-orange-500 px-2 py-1 rounded-full">
                                          <Thermometer className="h-3 w-3 mr-1" />
                                          FEVER
                                        </span>
                                      )}
                                      {patient.diabetes && (
                                        <span className="flex items-center text-xs font-bold text-white bg-purple-500 px-2 py-1 rounded-full">
                                          <Activity className="h-3 w-3 mr-1" />
                                          DM
                                        </span>
                                      )}
                                      {patient.htn && (
                                        <span className="flex items-center text-xs font-bold text-white bg-rose-500 px-2 py-1 rounded-full">
                                          <Heart className="h-3 w-3 mr-1" />
                                          HTN
                                        </span>
                                      )}
                                      {patient.review && (
                                        <span className="flex items-center text-xs font-bold text-white bg-indigo-500 px-2 py-1 rounded-full">
                                          <ClipboardList className="h-3 w-3 mr-1" />
                                          REVIEW
                                        </span>
                                      )}
                                      {!patient.diabetes && patient.name && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-2 text-purple-600 hover:bg-purple-100 text-xs rounded-full border border-purple-300"
                                          onClick={() => openAddToDmModal(patient)}
                                          title="Add to Diabetic Registry"
                                          data-testid={`add-to-dm-${patient.id}`}
                                        >
                                          <Activity className="h-3 w-3 mr-0.5" />
                                          +DM
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1.5 mt-2 md:mt-0 flex-wrap">
                              {/* Delete button for waiting/completed only */}
                              {(patient.status === 'waiting' || patient.status === 'completed') && (
                                <Button
                                  onClick={() => handleDeletePatient(patient.id, patient.token_number)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-9 md:h-8 px-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                  title="Delete entry"
                                  data-testid={`delete-patient-${patient.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                onClick={() => handlePrintToken(patient)}
                                variant="outline"
                                size="sm"
                                className="h-9 md:h-8 px-2 rounded-lg border-slate-300"
                                title="Print token"
                              >
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                              
                              <span
                                className={`px-2 py-1.5 rounded-lg text-xs font-bold border ${getStatusColor(patient.status)}`}
                                data-testid={`status-${patient.id}`}
                              >
                                {patient.status === 'waiting' ? 'Wait' : patient.status === 'in_cabin' ? 'Cabin' : patient.status === 'queued_next' ? 'NEXT' : 'ER'}
                              </span>

                              {patient.status === 'waiting' && !patient.urgent && (
                                <>
                                  <Button
                                    onClick={() => handleEnterCabin(patient.id)}
                                    data-testid={`enter-cabin-button-${patient.id}`}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 text-xs h-9 md:h-8 font-bold rounded-lg shadow"
                                  >
                                    Cabin
                                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                  </Button>
                                  {currentPatient && (
                                    <Button
                                      onClick={() => handleQueueNext(patient.id)}
                                      data-testid={`queue-next-button-${patient.id}`}
                                      className="bg-amber-500 hover:bg-amber-600 text-white px-2 py-1.5 text-xs h-9 md:h-8 font-bold rounded-lg shadow"
                                      title="Queue as next"
                                    >
                                      <FastForward className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </>
                              )}

                              {patient.status === 'queued_next' && (
                                <Button
                                  onClick={() => handleUnqueue(patient.id)}
                                  data-testid={`unqueue-button-${patient.id}`}
                                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 text-sm h-10 md:h-9 font-bold rounded-lg shadow"
                                >
                                  Cancel Next
                                </Button>
                              )}

                              {patient.status === 'waiting' && patient.urgent && (
                                <Button
                                  onClick={() => openERBedModal(patient.id, false)}
                                  data-testid={`enter-er-button-${patient.id}`}
                                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-xs h-9 md:h-8 font-bold rounded-lg shadow"
                                >
                                  ER <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                </Button>
                              )}

                              {patient.status === 'in_er' && (
                                <div className="flex items-center gap-1">
                                  <span className="px-3 py-2 rounded-lg text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                    ER {patient.er_bed ? `- ${patient.er_bed}` : ''}
                                  </span>
                                  <Button
                                    onClick={() => openERBedModal(patient.id, true)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-red-600 hover:bg-red-50"
                                    title="Change ER Bed"
                                    data-testid={`edit-er-bed-${patient.id}`}
                                  >
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    onClick={() => handleBackToWaiting(patient.id)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 px-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                                    title="Send back to waiting"
                                    data-testid={`er-back-to-waiting-staff-${patient.id}`}
                                  >
                                    <Undo2 className="h-3.5 w-3.5 mr-1" />
                                    <span className="text-xs">Waiting</span>
                                  </Button>
                                </div>
                              )}

                              {(patient.status === 'in_cabin' || patient.status === 'in_er') && (
                                <Button
                                  onClick={() => handleCloseConsultation(patient.id)}
                                  data-testid={`close-consultation-button-${patient.id}`}
                                  className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 text-xs h-9 md:h-8 font-bold rounded-lg shadow"
                                >
                                  Close {patient.status === 'in_er' ? 'ER' : 'Visit'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Completed Patients Section - At Bottom */}
                  {filteredPatients.filter(p => p.status === 'completed').length > 0 && (
                    <div className="mt-6 pt-4 border-t-2 border-slate-200">
                      <h3 className="text-sm font-bold text-green-700 uppercase tracking-wide flex items-center gap-2 mb-3">
                        <CheckCircle className="h-4 w-4" />
                        Completed ({filteredPatients.filter(p => p.status === 'completed').length})
                      </h3>
                      <div className="space-y-2">
                        {filteredPatients.filter(p => p.status === 'completed').map((patient) => (
                          <div
                            key={patient.id}
                            data-testid={`patient-card-${patient.id}`}
                            className="p-3 bg-green-50/50 border border-green-200 rounded-xl"
                          >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="bg-green-500 text-white min-w-[70px] p-2 rounded-lg text-center">
                                <span className="text-lg font-black" style={{ fontFamily: 'Arial Black, sans-serif' }}>
                                  {patient.token_number}
                                </span>
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">{patient.name || 'No Name'}</span>
                                <div className="text-xs text-slate-500">
                                  {patient.age} • {patient.sex}
                                  {patient.completed_at && (
                                    <span className="ml-2 text-green-600">
                                      ✓ {new Date(patient.completed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {patient.prescription_number && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`staff-completed-rx-${patient.id}`}>
                                  <FileText className="h-3 w-3" /> Rx {patient.prescription_number}
                                </span>
                              )}
                              {patient.diabetes && (
                                <span className="px-2 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-300">DM</span>
                              )}
                              {!patient.diabetes && patient.name && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-purple-600 hover:bg-purple-100 text-xs rounded-full border border-purple-300"
                                  onClick={() => openAddToDmModal(patient)}
                                  title="Add to Diabetic Registry"
                                  data-testid={`add-to-dm-completed-${patient.id}`}
                                >
                                  <Activity className="h-3 w-3 mr-0.5" />
                                  +DM
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-blue-600 hover:bg-blue-100 text-xs rounded-lg"
                                onClick={() => startEditing(patient)}
                                data-testid={`edit-vitals-completed-${patient.id}`}
                              >
                                <Edit2 className="h-3 w-3 mr-1" /> Vitals
                              </Button>
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-300">
                                Completed
                              </span>
                            </div>
                          </div>
                          {editingPatientId === patient.id && (
                            <div className="mt-3 space-y-2">
                              <VitalsEditGrid values={editingVitals} onChange={setEditingVitals} onFever={() => setEditingFever(true)} testId={`edit-vitals-${patient.id}`} />
                              <div className="flex gap-2">
                                <Button size="sm" data-testid={`edit-save-${patient.id}`} className="h-8 text-xs bg-green-500 hover:bg-green-600 text-white rounded-lg" onClick={() => handleUpdateName(patient.id)}>
                                  <Check className="h-3 w-3 mr-1" /> Save
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-300 rounded-lg" onClick={cancelEditing}>
                                  <X className="h-3 w-3 mr-1" /> Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
      {/* Daily Rx Start Modal */}

      {/* Pre-Registration Modal (Phone / Attender) */}
      {showPreRegModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="prereg-modal" onClick={() => preRegShowPlaceDropdown && setPreRegShowPlaceDropdown(false)}>
          <Card className="w-full max-w-md bg-white shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{preRegSource === 'phone' ? '📞' : '👤'}</span>
                <h3 className="text-lg font-bold text-slate-900">
                  {preRegSource === 'phone' ? 'Phone Booking' : 'Attender Registration'}
                </h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPreRegModal(false)} data-testid="prereg-close">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-xs text-slate-600 mb-3 bg-amber-50 border border-amber-200 rounded p-2">
              <b>No token yet</b> — token is issued only when patient physically arrives. They join the live queue in real arrival order.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Patient Name *</Label>
                <Input
                  value={preRegForm.name}
                  onChange={(e) => setPreRegForm({ ...preRegForm, name: e.target.value })}
                  placeholder="Patient name"
                  className="h-9"
                  autoFocus
                  data-testid="prereg-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Age</Label>
                  <Input
                    value={preRegForm.age}
                    onChange={(e) => setPreRegForm({ ...preRegForm, age: e.target.value })}
                    placeholder="e.g. 35 or 6m"
                    className="h-9"
                    data-testid="prereg-age-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Sex</Label>
                  <select
                    value={preRegForm.sex}
                    onChange={(e) => setPreRegForm({ ...preRegForm, sex: e.target.value })}
                    className="h-9 w-full border rounded px-2 text-sm"
                    data-testid="prereg-sex-input"
                  >
                    <option value="">—</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Phone</Label>
                  <div className="flex gap-1">
                    <Input
                      type="tel"
                      value={preRegForm.contact_number}
                      onChange={(e) => setPreRegForm({ ...preRegForm, contact_number: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchPreRegPhone(); } }}
                      placeholder="Phone"
                      className="h-9 flex-1"
                      data-testid="prereg-phone-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-9 px-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                      onClick={searchPreRegPhone}
                      disabled={preRegPhoneLoading}
                      data-testid="prereg-phone-search-btn"
                    >
                      <Search className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <Label className="text-xs">Place</Label>
                  <input
                    type="text"
                    value={preRegForm.place}
                    onChange={(e) => {
                      setPreRegForm({ ...preRegForm, place: e.target.value });
                      setPreRegPlaceFilter(e.target.value);
                      setPreRegShowPlaceDropdown(true);
                    }}
                    onFocus={() => setPreRegShowPlaceDropdown(true)}
                    placeholder="Select village..."
                    className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/20"
                    data-testid="prereg-place-input"
                    autoComplete="off"
                  />
                  {preRegShowPlaceDropdown && (
                    <div className="absolute z-50 top-[60px] left-0 right-0 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="prereg-place-dropdown">
                      {placesList
                        .filter(p => !preRegPlaceFilter || p.toLowerCase().includes(preRegPlaceFilter.toLowerCase()))
                        .map(p => (
                          <div
                            key={p}
                            onClick={() => selectPreRegPlace(p)}
                            className="px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50 transition-colors"
                            data-testid={`prereg-place-option-${p}`}
                          >
                            {p}
                          </div>
                        ))
                      }
                      <div
                        onClick={() => selectPreRegPlace('Other')}
                        className="px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 text-gray-500 border-t"
                      >
                        Other
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Phone Lookup — Family Members (previous visits) */}
              {preRegPhoneLoading && <div className="text-xs text-blue-500">Looking up phone...</div>}
              {preRegPhoneLookup && preRegPhoneLookup.family.length > 0 && (
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-200 space-y-1" data-testid="prereg-family-card">
                  <div className="text-xs font-bold text-blue-700">{preRegPhoneLookup.family.length} previous record{preRegPhoneLookup.family.length > 1 ? 's' : ''} — tap to fill</div>
                  <div className="max-h-28 overflow-y-auto space-y-0.5">
                    {preRegPhoneLookup.family.map((f, i) => (
                      <div
                        key={`prereg-fam-${i}-${f.name}`}
                        onClick={() => selectPreRegFamilyMember(f)}
                        className="flex items-center justify-between text-xs bg-white px-2 py-1.5 rounded border cursor-pointer hover:bg-blue-100 hover:border-blue-400 transition-colors"
                        data-testid={`prereg-family-member-${i}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-blue-800">{f.name}</span>
                          <span className="text-slate-500">{f.age} {f.sex}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {f.place && <span className="text-slate-400">{f.place}</span>}
                          <span className="text-blue-500 font-bold">{f.visits} visit{f.visits > 1 ? 's' : ''}</span>
                          <span className="text-slate-400">Last: {f.last_visit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowPreRegModal(false)} data-testid="prereg-cancel">Cancel</Button>
              <Button
                onClick={submitPreReg}
                className={preRegSource === 'phone' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                data-testid="prereg-save"
              >
                {preRegSource === 'phone' ? 'Save Booking' : 'Save Booking'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showRxAnchorModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="staff-rx-anchor-modal">
          <Card className="w-full max-w-md bg-white shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="h-6 w-6 text-emerald-700" />
              <h3 className="text-lg font-bold text-slate-900">Today's Rx Start</h3>
            </div>
            {rxAnchorInfo && (rxAnchorInfo.anchor || rxAnchorInfo.last_assigned) && (
              <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs space-y-1">
                {rxAnchorInfo.anchor && (
                  <div className="flex justify-between"><span className="text-slate-600">Anchor set:</span><b className="text-emerald-700">{rxAnchorInfo.anchor}</b></div>
                )}
                {rxAnchorInfo.last_assigned && (
                  <div className="flex justify-between"><span className="text-slate-600">Last assigned:</span><b>{rxAnchorInfo.last_assigned}</b></div>
                )}
                {rxAnchorInfo.next_to_assign && (
                  <div className="flex justify-between"><span className="text-slate-600">Next will be:</span><b className="text-rose-700">{rxAnchorInfo.next_to_assign}</b></div>
                )}
              </div>
            )}
            <p className="text-sm text-slate-600 mb-3">
              Enter the first Rx number from the doctor's pad. All today's regular &amp; exempt patients will auto-number from here.
            </p>
            <Input
              autoFocus
              value={rxAnchorValue}
              onChange={(e) => setRxAnchorValue(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') saveRxAnchor(); }}
              placeholder="e.g. A2571"
              className="text-lg font-bold tracking-wider text-center"
              maxLength={10}
              data-testid="staff-rx-anchor-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Format: Letter(s) + 1–5 digits. Editing later won't change already-assigned numbers.
            </p>
            <div className="flex justify-between gap-2 mt-5">
              {rxAnchorInfo?.anchor && (
                <Button
                  variant="outline"
                  onClick={clearRxAnchor}
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                  data-testid="staff-rx-anchor-clear"
                >
                  Clear
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => setShowRxAnchorModal(false)} data-testid="staff-rx-anchor-cancel">Cancel</Button>
                <Button onClick={saveRxAnchor} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="staff-rx-anchor-save">Save</Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      
      {/* Doctor Settings Modal */}
      {showSettings && (
        <DoctorSettings
          token={token}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAnnouncementModal(false)}>
          <Card className="p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">📢 TV Display Announcements</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Two lines at the bottom of TV display. Each line can be static or scrolling.
            </p>
            
            <div className="space-y-4">
              {/* Line 1 */}
              <div className="p-3 border rounded-lg bg-slate-50">
                <Label className="text-sm font-medium">Line 1 (Primary Message)</Label>
                <Input
                  value={announcement1}
                  onChange={(e) => setAnnouncement1(e.target.value)}
                  placeholder="Welcome message..."
                  className="mt-1"
                  data-testid="announcement-line1"
                />
                <div className="flex items-center gap-3 mt-2">
                  <Checkbox
                    id="scroll1-toggle"
                    checked={scroll1Enabled}
                    onCheckedChange={(checked) => setScroll1Enabled(checked)}
                  />
                  <Label htmlFor="scroll1-toggle" className="text-sm cursor-pointer">
                    Enable scrolling for Line 1
                  </Label>
                </div>
              </div>
              
              {/* Line 2 */}
              <div className="p-3 border rounded-lg bg-slate-50">
                <Label className="text-sm font-medium">Line 2 (Secondary Message)</Label>
                <Input
                  value={announcement2}
                  onChange={(e) => setAnnouncement2(e.target.value)}
                  placeholder="Additional info..."
                  className="mt-1"
                  data-testid="announcement-line2"
                />
                <div className="flex items-center gap-3 mt-2">
                  <Checkbox
                    id="scroll2-toggle"
                    checked={scroll2Enabled}
                    onCheckedChange={(checked) => setScroll2Enabled(checked)}
                  />
                  <Label htmlFor="scroll2-toggle" className="text-sm cursor-pointer">
                    Enable scrolling for Line 2
                  </Label>
                </div>
              </div>

              <p className="text-xs text-muted-foreground bg-amber-50 p-2 rounded">
                ⏰ Note: From 9 PM onwards, "OPD WILL BE CLOSED BY 10 PM" will automatically appear in Line 1.
              </p>
            </div>

            <div className="flex gap-2 mt-4">
              <Button onClick={saveAnnouncements} className="flex-1">
                Save Announcements
              </Button>
              <Button variant="outline" onClick={() => setShowAnnouncementModal(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ER Bed Selection Modal */}
      {showERBedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm bg-white shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <h2 className="text-lg font-bold text-red-700">
                    {erEditMode ? 'Change ER Bed' : 'Select ER Bed'}
                  </h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowERBedModal(false); setErPatientId(null); setErEditMode(false); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                {erEditMode ? 'Select a new bed for this patient' : 'Choose the bed to assign this patient in the ER'}
              </p>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {ER_BEDS.map((bed) => {
                  const isOccupied = occupiedBeds[bed] && occupiedBeds[bed].patient_id !== erPatientId;
                  const occupantInfo = occupiedBeds[bed];
                  
                  return (
                    <Button
                      key={bed}
                      onClick={() => erEditMode ? handleUpdateERBed(erPatientId, bed) : handleEnterER(erPatientId, bed)}
                      disabled={isOccupied}
                      className={`h-14 text-lg font-bold relative ${
                        isOccupied 
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300' 
                          : bed === 'OT' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                            bed === 'DB' ? 'bg-pink-600 hover:bg-pink-700 text-white' :
                            bed.startsWith('C') ? 'bg-orange-500 hover:bg-orange-600 text-white' :
                            'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                      data-testid={`er-bed-${bed}`}
                      title={isOccupied ? `Occupied by ${occupantInfo?.token_number || 'patient'}` : `Select ${bed}`}
                    >
                      <span>{bed}</span>
                      {isOccupied && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                          ✕
                        </span>
                      )}
                    </Button>
                  );
                })}
              </div>

              {/* Occupied beds legend */}
              {Object.keys(occupiedBeds).length > 0 && (
                <div className="text-xs bg-red-50 border border-red-200 rounded p-2 mb-3">
                  <div className="font-semibold text-red-700 mb-1">Occupied Beds:</div>
                  {Object.entries(occupiedBeds).map(([bed, info]) => (
                    <div key={bed} className="text-red-600">
                      {bed}: {info.token_number} {info.name ? `(${info.name})` : ''}
                    </div>
                  ))}
                </div>
              )}

              <div className="text-xs text-muted-foreground space-y-1 mb-4 bg-slate-50 p-2 rounded">
                <div><span className="font-semibold text-blue-600">B1, B2</span> - General Beds</div>
                <div><span className="font-semibold text-orange-600">C1, C2, C3</span> - Critical Care</div>
                <div><span className="font-semibold text-pink-600">DB</span> - Delivery Bed</div>
                <div><span className="font-semibold text-purple-600">OT</span> - Operation Theatre</div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setShowERBedModal(false); setErPatientId(null); setErEditMode(false); }}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Past Records Modal */}
      {showPastRecords && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-4xl bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <History className="h-6 w-6 text-blue-600" />
                  <h2 className="text-lg font-bold text-blue-700">Past Patient Records</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowPastRecords(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Date Navigation */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => changePastDate(-1)}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <Input
                    type="date"
                    value={pastRecordsDate}
                    onChange={(e) => {
                      setPastRecordsDate(e.target.value);
                      fetchPastRecords(e.target.value);
                    }}
                    max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                    className="w-40 h-8 text-center font-bold"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => changePastDate(1)}
                  className="h-8"
                  disabled={new Date(pastRecordsDate) >= new Date(Date.now() - 86400000)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {loadingPastRecords ? (
                <div className="text-center py-8 text-gray-500">Loading records...</div>
              ) : (
                <>
                  {/* Summary Cards */}
                  {pastSummary && (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-center">
                        <div className="text-2xl font-bold text-blue-700">{pastSummary.total}</div>
                        <div className="text-xs text-blue-600">Total Patients</div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
                        <div className="text-2xl font-bold text-green-700">{pastSummary.completed}</div>
                        <div className="text-xs text-green-600">Completed</div>
                      </div>
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-center">
                        <div className="text-2xl font-bold text-amber-700">{pastSummary.missed}</div>
                        <div className="text-xs text-amber-600">Pending/Missed</div>
                      </div>
                    </div>
                  )}

                  {/* Patient List */}
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    {pastPatients.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No patients found for {new Date(pastRecordsDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-semibold">Token</th>
                            <th className="text-left p-2 font-semibold">Name</th>
                            <th className="text-left p-2 font-semibold">Age/Sex</th>
                            <th className="text-left p-2 font-semibold">Status</th>
                            <th className="text-left p-2 font-semibold">Time</th>
                            <th className="text-left p-2 font-semibold">Flags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pastPatients.map((p, idx) => (
                            <tr key={p.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="p-2 font-bold text-blue-700">{p.token_number}</td>
                              <td className="p-2">{p.name || <span className="text-gray-400 italic">No name</span>}</td>
                              <td className="p-2">{p.age} {p.sex ? `/ ${p.sex}` : ''}</td>
                              <td className="p-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  p.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  p.status === 'in_er' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {p.status === 'completed' ? 'Done' : p.status === 'in_er' ? 'ER' : p.status}
                                </span>
                              </td>
                              <td className="p-2 text-xs text-gray-600">
                                {p.created_at && new Date(p.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-2">
                                <div className="flex gap-1 flex-wrap">
                                  {p.urgent && <span className="text-xs bg-red-500 text-white px-1 rounded">URG</span>}
                                  {p.fever && <span className="text-xs bg-orange-500 text-white px-1 rounded">FVR</span>}
                                  {p.review && <span className="text-xs bg-purple-500 text-white px-1 rounded">REV</span>}
                                  {p.diabetes && <span className="text-xs bg-indigo-500 text-white px-1 rounded">DM</span>}
                                  {p.htn && <span className="text-xs bg-rose-500 text-white px-1 rounded">HTN</span>}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setShowPastRecords(false)}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Day Modal */}
      {showDeleteDay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-6 w-6 text-red-600" />
                  <h2 className="text-lg font-bold text-red-700" data-testid="delete-day-title">Delete Patient Data</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowDeleteDay(false); setDeleteConfirmDate(null); setPasswordVerified(false); setDeletePassword(''); setPasswordError(''); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Password Gate */}
              {!passwordVerified ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Enter master password to access deletion</p>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && verifyDeletePassword()}
                    placeholder="Master password"
                    className="w-full h-10 border rounded-lg px-3 text-sm"
                    data-testid="delete-password-input"
                  />
                  {passwordError && <p className="text-xs text-red-600 font-medium">{passwordError}</p>}
                  <Button
                    onClick={verifyDeletePassword}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="verify-password-btn"
                  >
                    Verify Password
                  </Button>
                </div>
              ) : (
                <>
                  {deleteLoading && <div className="text-center py-6 text-gray-500">Loading...</div>}

                  {!deleteLoading && deleteDaySummary && (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {deleteDaySummary.patient_dates.length === 0 ? (
                        <p className="text-center text-gray-500 py-6">No patient data found</p>
                      ) : (
                        deleteDaySummary.patient_dates.map(item => (
                          <div key={item.date} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <span className="font-bold text-gray-800">
                                {new Date(item.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">({item.count} patients)</span>
                            </div>
                            {deleteConfirmDate === item.date ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                                  onClick={() => deletePatientsByDate(item.date)}
                                  data-testid={`confirm-delete-${item.date}`}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-8"
                                  onClick={() => setDeleteConfirmDate(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50 text-xs h-8"
                                onClick={() => setDeleteConfirmDate(item.date)}
                                data-testid={`delete-btn-${item.date}`}
                              >
                                <Trash2 className="h-3 w-3 mr-1" /> Delete
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => { setShowDeleteDay(false); setDeleteConfirmDate(null); setPasswordVerified(false); setDeletePassword(''); setPasswordError(''); }}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Places Manager Modal (password protected) */}
      {showPlacesManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-teal-600" />
                <h2 className="text-base font-bold text-gray-900" data-testid="places-manager-title">Manage Places</h2>
                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold">{placesList.length}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowPlacesManager(false); setPlacesUnlocked(false); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {!placesUnlocked ? (
                <div className="text-center py-6">
                  <MapPin className="h-10 w-10 mx-auto mb-3 text-teal-300" />
                  <p className="text-sm text-gray-600 mb-3">Enter master password to manage places</p>
                  <div className="flex gap-2 max-w-xs mx-auto">
                    <Input
                      type="password"
                      value={placesPassword}
                      onChange={(e) => setPlacesPassword(e.target.value)}
                      placeholder="Password"
                      className="h-9 text-sm"
                      data-testid="places-password-input"
                      onKeyDown={(e) => { if (e.key === 'Enter') setPlacesUnlocked(true); }}
                    />
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white h-9 px-4" onClick={() => setPlacesUnlocked(true)} data-testid="places-unlock-button">
                      Unlock
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Add new place */}
                  <div className="flex gap-2">
                    <Input
                      value={newPlaceName}
                      onChange={(e) => setNewPlaceName(e.target.value)}
                      placeholder="New place name..."
                      className="h-9 text-sm flex-1"
                      data-testid="new-place-input"
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && newPlaceName.trim()) {
                          const ok = await handleAddNewPlace(newPlaceName.trim(), placesPassword);
                          if (ok) setNewPlaceName('');
                        }
                      }}
                    />
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white h-9 px-4 text-sm"
                      onClick={async () => { const ok = await handleAddNewPlace(newPlaceName.trim(), placesPassword); if (ok) setNewPlaceName(''); }}
                      data-testid="add-place-button">
                      Add
                    </Button>
                  </div>
                  {/* Places list */}
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {placesList.map(p => (
                      <div key={p} className="flex items-center justify-between p-2 bg-white border rounded-lg text-sm hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-teal-500 flex-shrink-0" />
                          <span className="font-medium text-gray-800">{p}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 px-1 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDeletePlace(p, placesPassword)} data-testid={`delete-place-${p}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Review Reminders Modal */}
      {showReviews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-600" />
                <h2 className="text-base font-bold text-gray-900" data-testid="review-modal-title">Review Reminders</h2>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2 text-orange-700 border-orange-300 hover:bg-orange-50 text-xs"
                  onClick={printReviewPDF} data-testid="review-print-pdf">
                  <Download className="h-3 w-3 mr-1" /> PDF
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowReviews(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {reviewLoading && <div className="text-center py-8 text-gray-400">Loading...</div>}
              {!reviewLoading && reviewData && (
                <div className="space-y-4">
                  {/* Overdue */}
                  {reviewData.overdue.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">OVERDUE ({reviewData.overdue_count})</span>
                      </div>
                      <div className="space-y-1">
                        {reviewData.overdue.map((p, i) => (
                          <div key={p.id || i} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg text-xs">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-bold text-red-800 truncate">{p.name || 'Unknown'}</span>
                              <span className="text-gray-500 flex-shrink-0">{p.age} {p.sex}</span>
                              {p.diabetic_id && <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded font-bold flex-shrink-0">{p.diabetic_id}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {p.contact_number && <span className="text-blue-600">{p.contact_number}</span>}
                              <span className="font-bold text-red-600">{p.review_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Upcoming */}
                  {reviewData.upcoming.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-bold">UPCOMING — next 3 days ({reviewData.upcoming_count})</span>
                      </div>
                      <div className="space-y-1">
                        {reviewData.upcoming.map((p, i) => (
                          <div key={p.id || i} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-bold text-blue-800 truncate">{p.name || 'Unknown'}</span>
                              <span className="text-gray-500 flex-shrink-0">{p.age} {p.sex}</span>
                              {p.diabetic_id && <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded font-bold flex-shrink-0">{p.diabetic_id}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {p.contact_number && <span className="text-blue-600">{p.contact_number}</span>}
                              <span className="font-bold text-blue-700">{p.review_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {reviewData.upcoming.length === 0 && reviewData.overdue.length === 0 && (
                    <p className="text-center text-gray-400 py-8">No upcoming reviews</p>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Camp Patient Entry Modal */}
      {showCampEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-xl bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-700" />
                  <h2 className="text-base font-bold text-green-900" data-testid="camp-entry-title">Camp Patient Entry</h2>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">{campPatients.length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-7 px-2 text-green-700 border-green-300 hover:bg-green-50 text-xs" onClick={downloadCampPDF} data-testid="camp-download-pdf">
                    <Download className="h-3 w-3 mr-1" /> PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowCampEntry(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Add Patient Form */}
              <form onSubmit={handleAddCampPatient} className="space-y-2 mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-2">
                    <Label className="text-xs text-green-700">Camp Type</Label>
                    <select value={campType} onChange={(e) => setCampType(e.target.value)} className="w-full h-8 rounded-md border px-2 text-sm font-bold text-green-800" data-testid="camp-type-select">
                      {CAMP_TYPES.map(c => <option key={c.value} value={c.value}>{c.label} ({c.prefix})</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-green-700">Name</Label>
                    <Input value={campName} onChange={(e) => setCampName(e.target.value)} placeholder="Name" className="h-8 text-sm" data-testid="camp-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-green-700">Age / Sex</Label>
                    <div className="flex gap-1">
                      <Input value={campAge} onChange={(e) => setCampAge(e.target.value)} placeholder="Age" className="h-8 text-sm w-14" data-testid="camp-age-input" />
                      <select value={campSex} onChange={(e) => setCampSex(e.target.value)} className="h-8 rounded-md border px-1 text-sm w-12" data-testid="camp-sex-select">
                        <option value="">-</option>
                        <option value="Male">M</option>
                        <option value="Female">F</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input value={campPhone} onChange={(e) => setCampPhone(e.target.value)} placeholder="Phone" className="h-8 text-sm flex-1" />
                  <Input value={campPlace} onChange={(e) => setCampPlace(e.target.value)} placeholder="Place" className="h-8 text-sm flex-1" />
                  <Input value={campNotes} onChange={(e) => setCampNotes(e.target.value)} placeholder="Notes" className="h-8 text-sm flex-1" />
                  <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white h-8 px-4 text-sm" disabled={campLoading} data-testid="camp-add-button">
                    {campLoading ? '...' : 'Add'}
                  </Button>
                </div>
              </form>

              {/* Filter Bar */}
              <div className="flex flex-wrap gap-2 mb-3 items-center">
                <select value={campFilterType} onChange={(e) => { setCampFilterType(e.target.value); fetchCampPatients(e.target.value, campStartDate, campEndDate); }}
                  className="h-7 rounded border px-2 text-xs" data-testid="camp-filter-type">
                  <option value="">All Camps</option>
                  {CAMP_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input type="date" value={campStartDate} onChange={(e) => { setCampStartDate(e.target.value); if (campEndDate) fetchCampPatients(campFilterType, e.target.value, campEndDate); }}
                  className="h-7 rounded border px-2 text-xs" placeholder="From" />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" value={campEndDate} onChange={(e) => { setCampEndDate(e.target.value); if (campStartDate) fetchCampPatients(campFilterType, campStartDate, e.target.value); }}
                  className="h-7 rounded border px-2 text-xs" placeholder="To" />
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => { setCampFilterType(''); setCampStartDate(''); setCampEndDate(''); fetchCampPatients('', '', ''); }}>
                  Reset
                </Button>
              </div>

              {/* Camp Patients List */}
              <div className="max-h-52 overflow-y-auto space-y-1">
                {campPatients.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No camp patients found</p>
                ) : (
                  campPatients.map(cp => (
                    <div key={cp.id} className="flex items-center justify-between p-1.5 bg-white border rounded-lg text-sm">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="font-black text-green-700 bg-green-100 px-1.5 py-0.5 rounded text-xs flex-shrink-0">{cp.serial_number}</span>
                        <span className="font-bold text-gray-800 truncate">{cp.name || 'Unnamed'}</span>
                        <span className="text-xs text-gray-500 flex-shrink-0">{cp.age} {cp.sex}</span>
                        {cp.contact_number && <span className="text-xs text-blue-500 flex-shrink-0">{cp.contact_number}</span>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {cp.date !== new Date().toISOString().split('T')[0] && <span className="text-xs text-gray-400">{cp.date}</span>}
                        {cp.place && <span className="text-xs text-gray-400">{cp.place}</span>}
                        <Button variant="ghost" size="sm" className="h-5 px-1 text-red-400 hover:text-red-600"
                          onClick={() => handleDeleteCampPatient(cp.id, cp.serial_number)} data-testid={`delete-camp-patient-${cp.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">No consultation fee, excluded from census. Use PDF to print records.</p>
            </div>
          </Card>
        </div>
      )}

      {/* Diabetic Registry Modal */}
      {showDiabeticRegistry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-purple-700" />
                  <h2 className="text-lg font-bold text-purple-900" data-testid="dm-registry-title">Diabetic Registry</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowDiabeticRegistry(false); setSelectedDiabetic(null); setDiabeticSearch(''); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={diabeticSearch}
                  onChange={(e) => { setDiabeticSearch(e.target.value); fetchDiabeticRegistry(e.target.value); }}
                  placeholder="Search by name, ID, or phone..."
                  className="w-full h-9 pl-10 pr-3 border rounded-lg text-sm"
                  data-testid="dm-search-input"
                />
              </div>

              {/* Patient Detail View */}
              {selectedDiabetic ? (
                <div className="space-y-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDiabetic(null)} className="text-purple-700">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back to list
                  </Button>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-black text-purple-900">{selectedDiabetic.diabetic_id}</span>
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full font-bold">Diabetic</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Name</span><span className="font-bold">{selectedDiabetic.name}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Age</span><span className="font-bold">{selectedDiabetic.age || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Sex</span><span className="font-bold">{selectedDiabetic.sex || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Contact</span><span className="font-bold">{selectedDiabetic.contact_number || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Place</span><span className="font-bold">{selectedDiabetic.place || '-'}</span></div>
                    </div>
                    <div className="mt-4">
                      <h4 className="font-bold text-purple-800 text-sm mb-2">Visit History ({selectedDiabetic.visit_dates?.length || 0} visits)</h4>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {(selectedDiabetic.visit_dates || []).sort().reverse().map((vd, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-white px-3 py-2 rounded border">
                            <Calendar className="h-3 w-3 text-purple-500" />
                            <span>{new Date(vd).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 mb-2">
                    Total: {diabeticList.length} patients
                  </div>
                  {diabeticLoading && <div className="text-center py-6 text-gray-500">Loading...</div>}
                  {!diabeticLoading && (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {diabeticList.length === 0 ? (
                        <p className="text-center text-gray-500 py-6">No diabetic patients registered</p>
                      ) : (
                        diabeticList.map(dm => (
                          <div
                            key={dm.diabetic_id}
                            onClick={() => setSelectedDiabetic(dm)}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                            data-testid={`dm-entry-${dm.diabetic_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-black text-purple-800 text-sm bg-purple-100 px-2 py-1 rounded">{dm.diabetic_id}</span>
                              <div>
                                <span className="font-bold text-gray-800 text-sm">{dm.name}</span>
                                <div className="text-xs text-gray-500">{dm.place || ''} {dm.contact_number ? `| ${dm.contact_number}` : ''}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-bold text-purple-700">{dm.visit_dates?.length || 0} visits</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              <Button variant="outline" className="w-full mt-4" onClick={() => { setShowDiabeticRegistry(false); setSelectedDiabetic(null); setDiabeticSearch(''); }}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add to DM Modal - for adding existing patients to DM registry */}
      {showAddToDmModal && addToDmPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-purple-700" />
                  <h2 className="text-base font-bold text-purple-900" data-testid="add-to-dm-modal-title">Add to DM Registry</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddToDmModal(false); setAddToDmPatient(null); setAddToDmSearch(''); setAddToDmResults([]); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Patient info */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mb-4">
                <div className="flex items-center gap-2">
                  <span className="font-black text-blue-700 text-sm">{addToDmPatient.token_number}</span>
                  <span className="font-bold text-slate-800">{addToDmPatient.name}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{addToDmPatient.age} &bull; {addToDmPatient.sex}</div>
              </div>

              {/* Search existing DM or Register New */}
              <label className="text-xs font-bold text-purple-700 block mb-1">Search existing DM patient (ID / Phone / Name)</label>
              <Input
                type="text"
                value={addToDmSearch}
                onChange={(e) => searchAddToDm(e.target.value)}
                placeholder="Search by DM ID, phone, or name..."
                className="h-9 text-sm mb-2"
                data-testid="add-to-dm-search"
              />

              {addToDmLoading && <div className="text-xs text-purple-500 py-1">Searching...</div>}
              {addToDmResults.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1 mb-3">
                  {addToDmResults.map(dm => (
                    <div
                      key={dm.diabetic_id}
                      onClick={() => linkPatientToDm(addToDmPatient.id, dm.diabetic_id)}
                      className="flex items-center justify-between p-2 bg-white rounded border cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                      data-testid={`add-to-dm-result-${dm.diabetic_id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-black text-purple-800 text-xs bg-purple-100 px-1.5 py-0.5 rounded">{dm.diabetic_id}</span>
                        <div>
                          <span className="font-bold text-gray-800 text-xs">{dm.name}</span>
                          <div className="text-xs text-gray-500">{dm.contact_number || ''} {dm.place ? `| ${dm.place}` : ''}</div>
                        </div>
                      </div>
                      <span className="text-xs text-purple-600 font-bold">Link &rarr;</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-3 mt-2">
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  onClick={() => registerPatientAsDm(addToDmPatient.id)}
                  data-testid="register-new-dm-button"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Register as New DM Patient
                </Button>
                <p className="text-xs text-gray-500 text-center mt-1">Creates a new DM registry entry with auto-generated ID</p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StaffDashboard;